import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, animate, useMotionValue, AnimatePresence } from 'framer-motion';
import {
  FaGraduationCap, FaUsers, FaCalendarAlt, FaShieldAlt,
  FaChalkboardTeacher, FaClock, FaComments, FaStar,
  FaArrowRight, FaPlay, FaCheck, FaGlobe, FaBrain, FaFilter, FaChartLine,
  FaEye, FaEyeSlash, FaHeart, FaSearch, FaVideo, FaFileAlt, FaComment, FaSignInAlt,
  FaDatabase, FaLink, FaSitemap, FaLayerGroup, FaBolt, FaLightbulb, FaBullseye,
  FaDesktop, FaMobile, FaLaptop, FaCamera, FaBook, FaUserGraduate, FaCertificate,
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane
} from 'react-icons/fa';
import logo from './logo.png';
import studentImage from './student.png';
import teacherImage from './teacher_new.png';

import { supabase } from './supabaseClient';
import { getMultipleTeacherRatings } from './bookingService';
import OurTeachersSection from './OurTeachersSection';
import SEO from './components/SEO';
import { generateOrganizationSchema, generateWebsiteSchema, generateFAQSchema } from './utils/structuredData';
import { toArabicNumerals } from './utils/arabicNumbers';

// Animated counter component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Fade in animation wrapper
const FadeInSection = ({ children, delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const variants = {
    hidden: {
      opacity: 0,
      y: direction === 'up' ? 40 : direction === 'down' ? -40 : 0,
      x: direction === 'left' ? 40 : direction === 'right' ? -40 : 0
    },
    visible: { opacity: 1, y: 0, x: 0 }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
};

// Animated Process Graph Component
const AnimatedProcessGraph = ({ steps, isInView, isArabic }) => {
  const [animatedValues, setAnimatedValues] = useState(steps.map(() => 0));

  useEffect(() => {
    if (isInView) {
      steps.forEach((_, idx) => {
        setTimeout(() => {
          setAnimatedValues(prev => {
            const newValues = [...prev];
            newValues[idx] = 100;
            return newValues;
          });
        }, idx * 300);
      });
    }
  }, [isInView, steps.length]);

  // Graph data points - fixed percentages: 75%, 80%, 85%, 90%
  const percentages = [75, 80, 85, 90];
  const graphData = steps.map((step, idx) => ({
    ...step,
    value: percentages[idx],
    x: idx * 20 + 15, // X position in percentage (15, 35, 55, 75)
    barHeight: percentages[idx], // Height of bar from bottom
    y: 100 - percentages[idx] // Y position for line graph (top of bar)
  }));

  return (
    <div style={{
      background: 'white',
      borderRadius: 24,
      padding: 40,
      border: '1px solid #E2E8F0',
      width: '100%',
      maxWidth: 500,
      position: 'relative',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Graph Title */}
      <div style={{
        marginBottom: 32,
        textAlign: 'center'
      }}>
        <h3 style={{
          fontSize: 18,
          fontWeight: 700,
          color: '#0F172A',
          marginBottom: 8
        }}>
          {isArabic ? 'عملية التعلم' : 'Learning Process'}
        </h3>
        <p style={{
          fontSize: 14,
          color: '#64748B',
          margin: 0
        }}>
          {isArabic ? 'رحلة تعليمية سلسة' : 'Seamless learning journey'}
        </p>
      </div>

      {/* Animated Graph */}
      <div style={{
        position: 'relative',
        height: 320,
        width: '100%',
        padding: '20px 0'
      }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          style={{ overflow: 'visible' }}
        >
          {/* Grid lines - more spacing, lighter */}
          {[20, 40, 60, 80].map((y) => (
            <line
              key={y}
              x1="8"
              y1={y}
              x2="92"
              y2={y}
              stroke="#F1F5F9"
              strokeWidth="0.15"
              strokeDasharray="0.5,2"
            />
          ))}

          {/* Gradient definition */}
          <defs>
            <linearGradient id="barGradient" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#FF7426" stopOpacity="1" />
              <stop offset="100%" stopColor="#FF9854" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF7426" stopOpacity="1" />
              <stop offset="50%" stopColor="#FF9854" stopOpacity="1" />
              <stop offset="100%" stopColor="#FF7426" stopOpacity="1" />
            </linearGradient>
          </defs>

          {/* Animated bars - wider and more prominent */}
          {graphData.map((point, idx) => (
            <g key={`bar-${idx}`}>
              <motion.rect
                x={point.x - 4}
                y={100 - point.barHeight}
                width="8"
                height={point.barHeight}
                fill="url(#barGradient)"
                rx="2"
                initial={{ height: 0, y: 100, opacity: 0 }}
                animate={isInView ? {
                  height: point.barHeight,
                  y: 100 - point.barHeight,
                  opacity: 1
                } : {}}
                transition={{
                  delay: idx * 0.2,
                  duration: 0.8,
                  ease: "easeOut"
                }}
              />
            </g>
          ))}

          {/* Animated line graph overlaid on bars - lighter and thinner */}
          <motion.polyline
            points={graphData.map((point) =>
              `${point.x},${point.y}`
            ).join(' ')}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeOpacity="0.9"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 0.9 } : {}}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeInOut" }}
          />

          {/* Data point circles on line - smaller */}
          {graphData.map((point, idx) => (
            <motion.circle
              key={`circle-${idx}`}
              cx={point.x}
              cy={point.y}
              r="3"
              fill="#FF7426"
              stroke="white"
              strokeWidth="1.5"
              initial={{ scale: 0, opacity: 0 }}
              animate={isInView ? { scale: 1, opacity: 1 } : {}}
              transition={{
                delay: idx * 0.2 + 0.6,
                duration: 0.4,
                type: "spring",
                stiffness: 200
              }}
            />
          ))}

          {/* Percentage labels above points - smaller text */}
          {graphData.map((point, idx) => (
            <motion.text
              key={`label-${idx}`}
              x={point.x}
              y={point.y - 5}
              textAnchor="middle"
              fontSize="7"
              fontWeight="700"
              fill="#FF7426"
              initial={{ opacity: 0, y: point.y - 2 }}
              animate={isInView ? { opacity: 1, y: point.y - 5 } : {}}
              transition={{ delay: idx * 0.2 + 0.8 }}
            >
              {point.value}%
            </motion.text>
          ))}
        </svg>
      </div>

      {/* Step indicators below graph - outlined in orange */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 20,
        gap: 10
      }}>
        {steps.map((step, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: idx * 0.2 + 1 }}
            style={{
              flex: 1,
              textAlign: 'center',
              padding: '14px 10px',
              background: 'white',
              borderRadius: 12,
              border: '2px solid #FF7426',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(255, 116, 38, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 116, 38, 0.05) 0%, rgba(255, 152, 84, 0.05) 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 116, 38, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 116, 38, 0.1)';
            }}
          >
            <div style={{
              fontSize: 11,
              fontWeight: 800,
              color: '#FF7426',
              marginBottom: 5
            }}>
              {step.num}
            </div>
            <div style={{
              fontSize: 10,
              fontWeight: 600,
              color: '#0F172A',
              lineHeight: 1.4
            }}>
              {step.title}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// How It Works Section Component - Redesigned to match reference
const HowItWorksSection = ({ lang, isArabic, isMobile, navigate, t, isStudent = true }) => {
  const sectionRef = useRef(null);
  const visualRef = useRef(null);
  const sectionInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const visualInView = useInView(visualRef, { once: true, margin: "-100px" });
  const [activeStep, setActiveStep] = useState(0);
  const [emailText, setEmailText] = useState('');
  const [passwordText, setPasswordText] = useState('');
  const [animationStage, setAnimationStage] = useState('idle'); // idle, typing-email, typing-password, clicking-signin, completed
  const [bookingStage, setBookingStage] = useState('idle'); // idle, selecting-teacher, selecting-date, selecting-subject, completed
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [sessionStage, setSessionStage] = useState('idle'); // idle, joining, connected, active
  const [sessionTime, setSessionTime] = useState(0);
  // Teacher-specific states
  const [requestStage, setRequestStage] = useState('idle'); // idle, receiving, accepting
  const [requestDetailsStage, setRequestDetailsStage] = useState('idle'); // idle, viewing
  const [studentName, setStudentName] = useState('');
  const [requestSubject, setRequestSubject] = useState('');
  const [requestTime, setRequestTime] = useState('');
  const [requestDate, setRequestDate] = useState('');
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const bookingRef = useRef(null);
  const sessionRef = useRef(null);

  // Steps data for the right side
  const stepsData = isStudent ? [
    {
      num: 1,
      title: isArabic ? 'استخدم أدواتنا لإنشاء حسابك' : 'Use our tools to create your account',
      desc: isArabic
        ? 'ستتم إرشادك عبر خطوات لإضافة تفاصيل حسابك واختيار تفضيلاتك. يمكنك تحديث المعلومات في أي وقت.'
        : "You'll be guided by prompts to add account details and set your preferences. Make updates anytime.",
      tip: isArabic ? 'نصائح لبدء حسابك' : 'Get tips for starting your account'
    },
    {
      num: 2,
      title: isArabic ? 'ابحث عن المعلمين المناسبين' : 'Find the right teachers',
      desc: isArabic
        ? 'استخدم رابط البحث الخاص بك واستخدم الموارد في لوحة التحكم لاكتشاف المعلمين المناسبين.'
        : "Use our search tools and resources in your dashboard to discover teachers that match your needs."
    },
    {
      num: 3,
      title: isArabic ? 'احجز الجلسات بأمان' : 'Book sessions securely',
      desc: isArabic
        ? 'أضف معلومات الدفع الخاصة بك، أو دع المعلم يضيف معلوماته، وابدأ في حجز الجلسات التعليمية.'
        : "Add your payment information, and start booking learning sessions with verified teachers."
    }
  ] : [
    {
      num: 1,
      title: isArabic ? 'الطالب يرسل طلباً والمعلم يقبله' : 'Student sends request and teacher accepts',
      desc: isArabic
        ? 'يتلقى المعلم طلبات الجلسات من الطلاب ويمكنه قبول أو رفض الطلبات بناءً على توافره.'
        : "Teachers receive session requests from students and can accept or decline requests based on their availability.",
      tip: isArabic ? 'نصائح لإدارة الطلبات' : 'Get tips for managing requests'
    },
    {
      num: 2,
      title: isArabic ? 'المعلم يرى تفاصيل الطلب والتوقيت' : 'Teacher sees request details and timing',
      desc: isArabic
        ? 'يراجع المعلم تفاصيل الطلب بما في ذلك المادة والوقت المطلوب والتاريخ قبل قبول الجلسة.'
        : "Teacher reviews request details including subject, requested time, and date before accepting the session."
    },
    {
      num: 3,
      title: isArabic ? 'المعلم ينضم إلى الجلسة' : 'Teacher joins the session',
      desc: isArabic
        ? 'بعد قبول الطلب، ينضم المعلم إلى الجلسة في الوقت المحدد لبدء التدريس مع الطالب.'
        : "After accepting the request, the teacher joins the session at the scheduled time to start teaching with the student."
    }
  ];

  // Auto-typing animation sequence (Student) OR Request acceptance animation (Teacher)
  useEffect(() => {
    if (!visualInView) return;

    if (isStudent) {
      // Student flow: sign-in animation
      if (animationStage !== 'idle' && animationStage !== 'typing-email') return;
      if (activeStep !== 0 && animationStage === 'idle') return; // Only start if on step 0

      const email = 'student@example.com';
      const password = '••••••••';

      // Start typing email after 1 second
      const emailTimeout = setTimeout(() => {
        if (animationStage !== 'idle' && animationStage !== 'typing-email') return;
        setAnimationStage('typing-email');
        let emailIndex = 0;
        const emailInterval = setInterval(() => {
          if (emailIndex < email.length) {
            setEmailText(email.substring(0, emailIndex + 1));
            emailIndex++;
          } else {
            clearInterval(emailInterval);
            // Wait a bit, then start typing password
            setTimeout(() => {
              setAnimationStage('typing-password');
              let passwordIndex = 0;
              const passwordInterval = setInterval(() => {
                if (passwordIndex < password.length) {
                  setPasswordText(password.substring(0, passwordIndex + 1));
                  passwordIndex++;
                } else {
                  clearInterval(passwordInterval);
                  // Wait a bit, then click sign in
                  setTimeout(() => {
                    setAnimationStage('clicking-signin');
                    // After button animation, highlight step 2
                    setTimeout(() => {
                      setAnimationStage('completed');
                      setActiveStep(1); // Highlight step 2 (index 1)
                      // Start booking animation after a short delay
                      setTimeout(() => {
                        setBookingStage('selecting-teacher');
                      }, 1500);
                    }, 800);
                  }, 1000);
                }
              }, 100);
            }, 1500);
          }
        }, 80);
      }, 1000);

      return () => {
        clearTimeout(emailTimeout);
      };
    } else {
      // Teacher flow: request acceptance animation
      if (requestStage !== 'idle' && requestStage !== 'receiving') return;
      if (activeStep !== 0 && requestStage === 'idle') return; // Only start if on step 0

      const students = isArabic
        ? ['أحمد محمد', 'فاطمة علي', 'خالد حسن']
        : ['Ahmed Mohamed', 'Fatima Ali', 'Khalid Hassan'];

      // Start receiving request after 1 second
      const requestTimeout = setTimeout(() => {
        setRequestStage('receiving');
        setStudentName(students[0]);
        // After showing request, accept it
        setTimeout(() => {
          setRequestStage('accepting');
          // After accepting, move to step 2 (request details)
          setTimeout(() => {
            setActiveStep(1);
            setRequestDetailsStage('viewing');
            setRequestSubject(isArabic ? 'الرياضيات' : 'Mathematics');
            setRequestDate('2024-03-15');
            setRequestTime('10:00 AM');
            // After viewing details, move to step 3 (join session)
            setTimeout(() => {
              setActiveStep(2);
              setSessionStage('joining');
            }, 3000);
          }, 1500);
        }, 2000);
      }, 1000);

      return () => {
        clearTimeout(requestTimeout);
      };
    }
  }, [visualInView, isStudent, animationStage, requestStage, activeStep, isArabic]);

  // Booking animation sequence for step 2
  useEffect(() => {
    if (bookingStage === 'idle') return;

    const teachers = isArabic
      ? ['أحمد محمد', 'فاطمة علي', 'خالد حسن']
      : ['Ahmed Mohamed', 'Fatima Ali', 'Khalid Hassan'];
    const dates = isArabic
      ? ['2024-03-15', '2024-03-16', '2024-03-17']
      : ['2024-03-15', '2024-03-16', '2024-03-17'];
    const subjects = isArabic
      ? ['الرياضيات', 'العلوم', 'اللغة الإنجليزية']
      : ['Mathematics', 'Science', 'English'];

    if (bookingStage === 'selecting-teacher') {
      // Animate teacher selection
      let teacherIndex = 0;
      const teacherInterval = setInterval(() => {
        if (teacherIndex < teachers.length) {
          setSelectedTeacher(teachers[teacherIndex]);
          teacherIndex++;
        } else {
          clearInterval(teacherInterval);
          // Select final teacher and move to date
          setSelectedTeacher(teachers[0]);
          setTimeout(() => {
            setBookingStage('selecting-date');
          }, 1000);
        }
      }, 500);
      return () => clearInterval(teacherInterval);
    }

    if (bookingStage === 'selecting-date') {
      // Animate date selection
      let dateIndex = 0;
      const dateInterval = setInterval(() => {
        if (dateIndex < dates.length) {
          setSelectedDate(dates[dateIndex]);
          dateIndex++;
        } else {
          clearInterval(dateInterval);
          // Select final date and move to subject
          setSelectedDate(dates[1]);
          setTimeout(() => {
            setBookingStage('selecting-subject');
          }, 1000);
        }
      }, 500);
      return () => clearInterval(dateInterval);
    }

    if (bookingStage === 'selecting-subject') {
      // Animate subject selection
      let subjectIndex = 0;
      const subjectInterval = setInterval(() => {
        if (subjectIndex < subjects.length) {
          setSelectedSubject(subjects[subjectIndex]);
          subjectIndex++;
        } else {
          clearInterval(subjectInterval);
          // Select final subject
          setSelectedSubject(subjects[0]);
          setTimeout(() => {
            setBookingStage('completed');
            // After booking is complete, switch to step 3
            setTimeout(() => {
              setActiveStep(2); // Switch to step 3 (index 2)
              setSessionStage('joining');
            }, 2000);
          }, 1000);
        }
      }, 500);
      return () => clearInterval(subjectInterval);
    }
  }, [bookingStage, isArabic]);

  // Session joining animation sequence for step 3
  useEffect(() => {
    if (sessionStage === 'joining') {
      // After 3 seconds of joining, transition to connected/active state
      const joinTimeout = setTimeout(() => {
        setSessionStage('connected');
        // After another 2 seconds, show active session
        setTimeout(() => {
          setSessionStage('active');
          // Start session timer
          let time = 0;
          const timerInterval = setInterval(() => {
            time += 1;
            setSessionTime(time);
            // After 5 seconds of active session, return to step 1
            if (time >= 2) {
              clearInterval(timerInterval);
              // Reset all states and restart animation sequence
              setTimeout(() => {
                setActiveStep(0);
                setEmailText('');
                setPasswordText('');
                setAnimationStage('idle');
                setBookingStage('idle');
                setSelectedTeacher('');
                setSelectedDate('');
                setSelectedSubject('');
                setSessionStage('idle');
                setSessionTime(0);
                // Reset teacher-specific states
                setRequestStage('idle');
                setRequestDetailsStage('idle');
                setStudentName('');
                setRequestSubject('');
                setRequestTime('');
                setRequestDate('');

                // Restart animation sequence after reset
                setTimeout(() => {
                  if (isStudent) {
                    // Reset to idle first, then start typing
                    setAnimationStage('idle');
                    setTimeout(() => {
                      setAnimationStage('typing-email');
                    }, 100);
                  } else {
                    // Reset to idle first, then start receiving
                    setRequestStage('idle');
                    setStudentName('');
                    setTimeout(() => {
                      setRequestStage('receiving');
                      const students = isArabic
                        ? ['أحمد محمد', 'فاطمة علي', 'خالد حسن']
                        : ['Ahmed Mohamed', 'Fatima Ali', 'Khalid Hassan'];
                      setStudentName(students[0]);
                    }, 200);
                  }
                }, 1000);
              }, 1000);
            }
          }, 1000);
          return () => clearInterval(timerInterval);
        }, 2000);
      }, 3000);
      return () => clearTimeout(joinTimeout);
    }
  }, [sessionStage]);

  return (
    <section id="how-it-works-section" style={{
      background: '#F8FAFC',
      padding: isMobile ? '60px 16px' : '100px 40px'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 32 : 60,
          alignItems: 'center'
        }}>
          {/* Left: Interactive Visual Panel */}
          <FadeInSection delay={0.2}>
            <div
              ref={visualRef}
              style={{
                order: isMobile ? -1 : (isArabic ? 2 : 1),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative'
              }}
            >
              {/* Sign In Form (Student) OR Request Acceptance (Teacher) - shown when step 0 */}
              {activeStep === 0 && isStudent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={visualInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6 }}
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    background: 'white',
                    borderRadius: 24,
                    padding: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  {/* Welcome Heading */}
                  <h2 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#0F172A',
                    marginBottom: 8
                  }}>
                    {isArabic ? 'مرحباً بعودتك' : 'Welcome back'}
                  </h2>
                  <p style={{
                    fontSize: 15,
                    color: '#64748B',
                    marginBottom: 32
                  }}>
                    {isArabic
                      ? 'قم بتسجيل الدخول كطالب للمتابعة إلى لوحة التحكم الخاصة بك'
                      : 'Sign in as a student to continue to your dashboard'}
                  </p>

                  {/* Email Field */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      {isArabic ? 'عنوان البريد الإلكتروني' : 'Email Address'}
                    </label>
                    <motion.input
                      type="email"
                      placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      value={emailText}
                      readOnly
                      animate={{
                        borderColor: animationStage === 'typing-email' ? '#FF7426' : '#E2E8F0',
                        boxShadow: animationStage === 'typing-email' ? '0 0 0 3px rgba(255, 116, 38, 0.1)' : 'none'
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid #E2E8F0',
                        fontSize: 15,
                        color: '#0F172A',
                        background: 'white',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>

                  {/* Password Field */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      {isArabic ? 'كلمة المرور' : 'Password'}
                    </label>
                    <div style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <motion.input
                        type="password"
                        placeholder={isArabic ? 'أدخل كلمة المرور' : 'Enter your password'}
                        value={passwordText}
                        readOnly
                        animate={{
                          borderColor: animationStage === 'typing-password' ? '#FF7426' : '#E2E8F0',
                          boxShadow: animationStage === 'typing-password' ? '0 0 0 3px rgba(255, 116, 38, 0.1)' : 'none'
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          paddingRight: '40px',
                          borderRadius: 12,
                          border: '1px solid #E2E8F0',
                          fontSize: 15,
                          color: '#0F172A',
                          background: 'white',
                          outline: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        right: 12,
                        color: '#64748B',
                        cursor: 'pointer'
                      }}>
                        <FaEyeSlash size={16} />
                      </div>
                    </div>
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      color: '#0F172A',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        readOnly
                        style={{
                          width: 16,
                          height: 16,
                          cursor: 'pointer'
                        }}
                      />
                      {isArabic ? 'تذكرني' : 'Remember me'}
                    </label>
                    <a
                      href="/signin"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/signin');
                      }}
                      style={{
                        fontSize: 14,
                        color: '#FF7426',
                        textDecoration: 'none',
                        fontWeight: 600
                      }}
                    >
                      {isArabic ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
                    </a>
                  </div>

                  {/* Sign In Button */}
                  <motion.button
                    onClick={() => navigate('/signin')}
                    whileHover={{ scale: animationStage === 'completed' ? 1.02 : 1 }}
                    whileTap={{ scale: 0.98 }}
                    animate={{
                      scale: animationStage === 'clicking-signin' ? [1, 0.95, 1] : 1,
                      boxShadow: animationStage === 'clicking-signin'
                        ? ['0 4px 14px rgba(255, 116, 38, 0.4)', '0 2px 8px rgba(255, 116, 38, 0.6)', '0 4px 14px rgba(255, 116, 38, 0.4)']
                        : '0 4px 14px rgba(255, 116, 38, 0.4)'
                    }}
                    transition={{
                      duration: 0.3,
                      times: animationStage === 'clicking-signin' ? [0, 0.5, 1] : undefined
                    }}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: 12,
                      background: '#FF7426',
                      color: 'white',
                      border: 'none',
                      fontSize: 16,
                      fontWeight: 700,
                      cursor: 'pointer',
                      marginBottom: 24,
                      boxShadow: '0 4px 14px rgba(255, 116, 38, 0.4)'
                    }}
                  >
                    {isArabic ? 'تسجيل الدخول' : 'Sign In'}
                  </motion.button>

                  {/* Sign Up Link */}
                  <div style={{
                    textAlign: 'center',
                    fontSize: 14,
                    color: '#64748B'
                  }}>
                    {isArabic ? 'ليس لديك حساب؟ ' : "Don't have an account? "}
                    <a
                      href="/signup"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/signup');
                      }}
                      style={{
                        color: '#0F172A',
                        fontWeight: 700,
                        textDecoration: 'none'
                      }}
                    >
                      {isArabic ? 'سجل الآن' : 'Sign up'}
                    </a>
                  </div>
                </motion.div>
              )}

              {/* Teacher Request Acceptance Interface - shown when step 0 for teachers */}
              {activeStep === 0 && !isStudent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={visualInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6 }}
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    background: 'white',
                    borderRadius: 24,
                    padding: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  {/* Heading */}
                  <h2 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#0F172A',
                    marginBottom: 8
                  }}>
                    {isArabic ? 'طلبات الجلسات' : 'Session Requests'}
                  </h2>
                  <p style={{
                    fontSize: 15,
                    color: '#64748B',
                    marginBottom: 32
                  }}>
                    {isArabic
                      ? 'تلقيت طلب جلسة جديد من طالب'
                      : 'You have received a new session request from a student'}
                  </p>

                  {/* Request Card */}
                  <motion.div
                    animate={{
                      borderColor: requestStage === 'receiving' || requestStage === 'accepting' ? '#FF7426' : '#E2E8F0',
                      boxShadow: requestStage === 'receiving' || requestStage === 'accepting' ? '0 0 0 3px rgba(255, 116, 38, 0.1)' : 'none'
                    }}
                    transition={{ duration: 0.3 }}
                    style={{
                      border: '1px solid #E2E8F0',
                      borderRadius: 16,
                      padding: '24px',
                      marginBottom: 20,
                      background: requestStage === 'accepting' ? 'rgba(255, 116, 38, 0.05)' : 'white'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 16
                    }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 18
                      }}>
                        {studentName ? studentName.charAt(0).toUpperCase() : 'S'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#0F172A',
                          margin: 0,
                          marginBottom: 4
                        }}>
                          {studentName || (isArabic ? 'طالب جديد' : 'New Student')}
                        </h3>
                        <p style={{
                          fontSize: 14,
                          color: '#64748B',
                          margin: 0
                        }}>
                          {isArabic ? 'طلب جلسة جديدة' : 'New session request'}
                        </p>
                      </div>
                    </div>

                    {requestStage === 'accepting' && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                          padding: '12px',
                          background: '#10B981',
                          borderRadius: 8,
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 600,
                          textAlign: 'center'
                        }}
                      >
                        <FaCheck size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {isArabic ? 'تم قبول الطلب' : 'Request Accepted'}
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: 12
                  }}>
                    <motion.button
                      animate={{
                        scale: requestStage === 'accepting' ? [1, 0.95, 1] : 1,
                        boxShadow: requestStage === 'accepting'
                          ? ['0 4px 14px rgba(255, 116, 38, 0.4)', '0 2px 8px rgba(255, 116, 38, 0.6)', '0 4px 14px rgba(255, 116, 38, 0.4)']
                          : '0 4px 14px rgba(255, 116, 38, 0.4)'
                      }}
                      transition={{
                        duration: 0.3,
                        times: requestStage === 'accepting' ? [0, 0.5, 1] : undefined
                      }}
                      style={{
                        flex: 1,
                        padding: '14px',
                        borderRadius: 12,
                        background: '#FF7426',
                        color: 'white',
                        border: 'none',
                        fontSize: 16,
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: '0 4px 14px rgba(255, 116, 38, 0.4)'
                      }}
                    >
                      {isArabic ? 'قبول' : 'Accept'}
                    </motion.button>
                    <button
                      style={{
                        flex: 1,
                        padding: '14px',
                        borderRadius: 12,
                        background: 'white',
                        color: '#64748B',
                        border: '1px solid #E2E8F0',
                        fontSize: 16,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {isArabic ? 'رفض' : 'Decline'}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Booking Form (Student) OR Request Details (Teacher) - shown when step 1 */}
              {activeStep === 1 && isStudent && (
                <motion.div
                  ref={bookingRef}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    background: 'white',
                    borderRadius: 24,
                    padding: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  {/* Booking Heading */}
                  <h2 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#0F172A',
                    marginBottom: 8
                  }}>
                    {isArabic ? 'احجز جلسة' : 'Book a Session'}
                  </h2>
                  <p style={{
                    fontSize: 15,
                    color: '#64748B',
                    marginBottom: 32
                  }}>
                    {isArabic
                      ? 'اختر المعلم والتاريخ والموضوع'
                      : 'Select teacher, date, and subject'}
                  </p>

                  {/* Teacher Selection */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      <FaChalkboardTeacher size={16} color="#FF7426" />
                      {isArabic ? 'المعلم' : 'Teacher'}
                    </label>
                    <motion.div
                      animate={{
                        borderColor: bookingStage === 'selecting-teacher' ? '#FF7426' : '#E2E8F0',
                        boxShadow: bookingStage === 'selecting-teacher' ? '0 0 0 3px rgba(255, 116, 38, 0.1)' : 'none'
                      }}
                      transition={{ duration: 0.3 }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid #E2E8F0',
                        fontSize: 15,
                        color: selectedTeacher ? '#0F172A' : '#94A3B8',
                        background: 'white',
                        cursor: 'pointer',
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {selectedTeacher || (isArabic ? 'اختر المعلم' : 'Select teacher')}
                    </motion.div>
                  </div>

                  {/* Date Selection */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      <FaCalendarAlt size={16} color="#FF7426" />
                      {isArabic ? 'التاريخ' : 'Date'}
                    </label>
                    <motion.div
                      animate={{
                        borderColor: bookingStage === 'selecting-date' ? '#FF7426' : '#E2E8F0',
                        boxShadow: bookingStage === 'selecting-date' ? '0 0 0 3px rgba(255, 116, 38, 0.1)' : 'none'
                      }}
                      transition={{ duration: 0.3 }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid #E2E8F0',
                        fontSize: 15,
                        color: selectedDate ? '#0F172A' : '#94A3B8',
                        background: 'white',
                        cursor: 'pointer',
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {selectedDate ? new Date(selectedDate).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : (isArabic ? 'اختر التاريخ' : 'Select date')}
                    </motion.div>
                  </div>

                  {/* Subject Selection */}
                  <div style={{ marginBottom: 24 }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      <FaGraduationCap size={16} color="#FF7426" />
                      {isArabic ? 'الموضوع' : 'Subject'}
                    </label>
                    <motion.div
                      animate={{
                        borderColor: bookingStage === 'selecting-subject' ? '#FF7426' : '#E2E8F0',
                        boxShadow: bookingStage === 'selecting-subject' ? '0 0 0 3px rgba(255, 116, 38, 0.1)' : 'none'
                      }}
                      transition={{ duration: 0.3 }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid #E2E8F0',
                        fontSize: 15,
                        color: selectedSubject ? '#0F172A' : '#94A3B8',
                        background: 'white',
                        cursor: 'pointer',
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {selectedSubject || (isArabic ? 'اختر الموضوع' : 'Select subject')}
                    </motion.div>
                  </div>

                  {/* Book Button */}
                  <motion.button
                    animate={{
                      scale: bookingStage === 'completed' ? [1, 0.95, 1] : 1,
                      boxShadow: bookingStage === 'completed'
                        ? ['0 4px 14px rgba(255, 116, 38, 0.4)', '0 2px 8px rgba(255, 116, 38, 0.6)', '0 4px 14px rgba(255, 116, 38, 0.4)']
                        : '0 4px 14px rgba(255, 116, 38, 0.4)',
                      background: bookingStage === 'completed' ? '#10B981' : '#FF7426'
                    }}
                    transition={{
                      duration: 0.3,
                      times: bookingStage === 'completed' ? [0, 0.5, 1] : undefined
                    }}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: 12,
                      background: '#FF7426',
                      color: 'white',
                      border: 'none',
                      fontSize: 16,
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(255, 116, 38, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    {bookingStage === 'completed' ? (
                      <>
                        <FaCheck size={16} />
                        {isArabic ? 'تم الحجز' : 'Booked!'}
                      </>
                    ) : (
                      isArabic ? 'احجز الآن' : 'Book Now'
                    )}
                  </motion.button>
                </motion.div>
              )}

              {/* Teacher Request Details View - shown when step 1 for teachers */}
              {activeStep === 1 && !isStudent && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    background: 'white',
                    borderRadius: 24,
                    padding: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  {/* Request Details Heading */}
                  <h2 style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#0F172A',
                    marginBottom: 8
                  }}>
                    {isArabic ? 'تفاصيل الطلب' : 'Request Details'}
                  </h2>
                  <p style={{
                    fontSize: 15,
                    color: '#64748B',
                    marginBottom: 32
                  }}>
                    {isArabic
                      ? 'مراجعة تفاصيل طلب الجلسة'
                      : 'Review session request details'}
                  </p>

                  {/* Student Info */}
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: 16,
                    padding: '20px',
                    marginBottom: 20
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 16
                    }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 18
                      }}>
                        {studentName ? studentName.charAt(0).toUpperCase() : 'S'}
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#0F172A',
                          margin: 0
                        }}>
                          {studentName || (isArabic ? 'الطالب' : 'Student')}
                        </h3>
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      <FaGraduationCap size={16} color="#FF7426" />
                      {isArabic ? 'المادة' : 'Subject'}
                    </label>
                    <div style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1px solid #E2E8F0',
                      fontSize: 15,
                      color: '#0F172A',
                      background: '#F8FAFC'
                    }}>
                      {requestSubject || (isArabic ? 'الرياضيات' : 'Mathematics')}
                    </div>
                  </div>

                  {/* Date */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      <FaCalendarAlt size={16} color="#FF7426" />
                      {isArabic ? 'التاريخ' : 'Date'}
                    </label>
                    <div style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1px solid #E2E8F0',
                      fontSize: 15,
                      color: '#0F172A',
                      background: '#F8FAFC'
                    }}>
                      {requestDate ? new Date(requestDate).toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : (isArabic ? '15 مارس 2024' : 'March 15, 2024')}
                    </div>
                  </div>

                  {/* Time */}
                  <div style={{ marginBottom: 24 }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#0F172A',
                      marginBottom: 8
                    }}>
                      <FaClock size={16} color="#FF7426" />
                      {isArabic ? 'الوقت' : 'Time'}
                    </label>
                    <div style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 12,
                      border: '1px solid #E2E8F0',
                      fontSize: 15,
                      color: '#0F172A',
                      background: '#F8FAFC'
                    }}>
                      {requestTime || (isArabic ? '10:00 صباحاً' : '10:00 AM')}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Live Session Form - shown when step 3 is active */}
              {activeStep === 2 && (
                <motion.div
                  ref={sessionRef}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{ duration: 0.5 }}
                  style={{
                    width: '100%',
                    maxWidth: 500,
                    background: 'white',
                    borderRadius: 24,
                    padding: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #E2E8F0'
                  }}
                >
                  {/* Session Heading */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 24
                  }}>
                    <div>
                      <h2 style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: '#0F172A',
                        marginBottom: 4
                      }}>
                        {isArabic ? 'جلسة مباشرة' : 'Live Session'}
                      </h2>
                      <p style={{
                        fontSize: 14,
                        color: '#64748B'
                      }}>
                        {selectedTeacher || (isArabic ? 'أحمد محمد' : 'Ahmed Mohamed')}
                      </p>
                    </div>
                  </div>

                  {/* Video Container */}
                  <div style={{
                    width: '100%',
                    aspectRatio: '16/9',
                    borderRadius: 16,
                    background: sessionStage === 'joining' ? '#F8FAFC' : '#0F172A',
                    marginBottom: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid #E2E8F0'
                  }}>
                    {sessionStage === 'joining' && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        style={{
                          textAlign: 'center',
                          color: '#64748B'
                        }}
                      >
                        <div
                          style={{
                            marginBottom: 16,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%'
                          }}
                        >
                          <FaVideo size={48} color="#FF7426" />
                        </div>
                        <motion.p
                          animate={{ opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            margin: 0,
                            color: '#0F172A'
                          }}
                        >
                          {isArabic ? 'جارٍ الاتصال...' : 'Joining session...'}
                        </motion.p>
                        <motion.p
                          animate={{ opacity: [0.3, 0.7, 0.3] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                          style={{
                            fontSize: 13,
                            margin: '8px 0 0 0',
                            color: '#64748B'
                          }}
                        >
                          {isArabic ? 'يرجى الانتظار' : 'Please wait'}
                        </motion.p>
                      </motion.div>
                    )}
                    {sessionStage !== 'joining' && (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: '#0F172A'
                      }} />
                    )}
                  </div>

                  {/* Session Controls */}
                  <div style={{
                    display: 'flex',
                    gap: 12,
                    justifyContent: 'center'
                  }}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '12px 24px',
                        borderRadius: 12,
                        background: '#64748B',
                        color: 'white',
                        border: 'none',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <FaVideo size={14} />
                      {isArabic ? 'إلغاء' : 'Cancel'}
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </div>
          </FadeInSection>

          {/* Right: Steps List */}
          <FadeInSection>
            <div
              ref={sectionRef}
              style={{
                textAlign: isMobile ? 'center' : (isArabic ? 'right' : 'left'),
                order: isMobile ? 1 : (isArabic ? 1 : 2)
              }}
            >
              {/* Main Heading */}
              <motion.h2
                initial={{ opacity: 0, y: -20 }}
                animate={sectionInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6 }}
                style={{
                  fontSize: isMobile ? 24 : 36,
                  fontWeight: 700,
                  color: '#0F172A',
                  marginBottom: isMobile ? 20 : 24,
                  lineHeight: 1.3,
                  paddingLeft: isMobile ? 4 : 0,
                  paddingRight: isMobile ? 4 : 0
                }}
              >
                {isStudent ? (
                  isArabic
                    ? 'التعلم على تعلّم سهل وقوي وموثوق'
                    : 'Learning on Taealam is easy, powerful, and trusted'
                ) : (
                  isArabic
                    ? 'التدريس على تعلّم سهل وقوي وموثوق'
                    : 'Teaching on Taealam is easy, powerful, and trusted'
                )}
              </motion.h2>

              {/* Steps List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 24 : 32 }}>
                {stepsData.map((step, idx) => (
                  <motion.div
                    key={idx}
                    ref={idx === 1 ? step2Ref : (idx === 2 ? step3Ref : null)}
                    initial={{ opacity: 0, x: isArabic ? 20 : -20 }}
                    animate={{
                      ...(sectionInView ? { opacity: 1, x: 0 } : {})
                    }}
                    transition={{ delay: 0.2 + idx * 0.15, duration: 0.3 }}
                    onMouseEnter={() => setActiveStep(idx)}
                    style={{
                      display: 'flex',
                      gap: isMobile ? 16 : 20,
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      padding: isMobile ? '8px 4px' : '12px',
                      borderRadius: 12,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Step Number Circle */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <motion.div
                        animate={{
                          borderColor: '#0F172A',
                          background: 'white',
                          color: '#0F172A',
                          scale: 1
                        }}
                        transition={{ duration: 0.3 }}
                        style={{
                          width: isMobile ? 36 : 40,
                          height: isMobile ? 36 : 40,
                          borderRadius: '50%',
                          border: '2px solid #0F172A',
                          background: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isMobile ? 14 : 16,
                          fontWeight: 700,
                          color: '#0F172A'
                        }}
                      >
                        {step.num}
                      </motion.div>
                      {/* Arrow pointing to currently active step */}
                      {activeStep === idx && !isMobile && (
                        <motion.div
                          initial={{ opacity: 0, x: isArabic ? 20 : -20, scale: 0.5 }}
                          animate={{
                            opacity: 1,
                            x: [0, isArabic ? -4 : 4, 0],
                            scale: 1
                          }}
                          transition={{
                            duration: 0.4,
                            x: {
                              duration: 1.5,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: '35%',
                            [isArabic ? 'right' : 'left']: -24,
                            transform: 'translateY(-50%)',
                            color: '#FF7426'
                          }}
                        >
                          <motion.div
                            animate={{
                              scale: [1, 1.2, 1],
                              opacity: [1, 0.8, 1]
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <FaArrowRight
                              size={16}
                              style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }}
                            />
                          </motion.div>
                        </motion.div>
                      )}
                    </div>

                    {/* Step Content */}
                    <div style={{ flex: 1, textAlign: isMobile ? 'left' : (isArabic ? 'right' : 'left') }}>
                      <h3 style={{
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 700,
                        color: '#0F172A',
                        marginBottom: 6,
                        lineHeight: 1.3
                      }}>
                        {step.title}
                      </h3>
                      <p style={{
                        fontSize: isMobile ? 14 : 15,
                        color: '#475569',
                        lineHeight: 1.6,
                        margin: 0,
                        marginBottom: step.tip ? 8 : 0
                      }}>
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>
  );
};

// Taealam Features Visual - Framework Diagram Design
const TaealamFeaturesVisual = ({ isArabic }) => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-50px" });
  const [hoveredItem, setHoveredItem] = useState(null);
  const svgRef = useRef(null);

  // Strategic Objectives (Top Row) - Education Focused
  const strategicObjectives = [
    {
      id: 'transformation',
      title: isArabic ? 'تعليم فعال' : 'Effective Learning',
      desc: isArabic ? 'تحويل تجربة التعلم من خلال منصة متكاملة' : 'Transform learning through integrated platform',
      color: '#10B981'
    },
    {
      id: 'experience',
      title: isArabic ? 'تجربة الطالب' : 'Student Experience',
      desc: isArabic ? 'إدارة التعلم حسب الجلسات والمسار التعليمي' : 'Manage learning by sessions and progress',
      color: '#EC4899'
    },
    {
      id: 'design',
      title: isArabic ? 'منصة موحدة' : 'Unified Platform',
      desc: isArabic ? 'جميع أدوات التعلم في مكان واحد' : 'All learning tools in one place',
      color: '#8B5CF6'
    }
  ];

  // Core Processes (Middle Row - Diamond-shaped) - Teaching Platform Focused
  const coreProcesses = [
    {
      id: 'discovery',
      title: isArabic ? 'اكتشاف المعلمين' : 'Find Teachers',
      icon1: FaSearch,
      icon1Color: '#FBBF24',
      icon2: FaChalkboardTeacher,
      icon2Color: '#3B82F6',
      icon2Text: null,
      color: '#E5E7EB'
    },
    {
      id: 'alignment',
      title: isArabic ? 'حجز الجلسات' : 'Book Sessions',
      icon1: FaCalendarAlt,
      icon1Color: '#8B5CF6',
      icon2: FaVideo,
      icon2Color: '#10B981',
      color: '#E5E7EB'
    },
    {
      id: 'tracking',
      title: isArabic ? 'تتبع التقدم' : 'Track Progress',
      icon1: FaChartLine,
      icon1Color: '#10B981',
      icon2: FaCertificate,
      icon2Color: '#FF7426',
      color: '#E5E7EB'
    }
  ];

  // Foundational Capabilities (Bottom Row) - Platform Features
  const capabilities = [
    {
      id: 'framework',
      title: isArabic ? 'منصة التعلم' : 'Learning Platform',
      icon: FaGraduationCap,
      color: '#8B5CF6'
    },
    {
      id: 'taxonomy',
      title: isArabic ? 'أنواع الجلسات' : 'Session Types',
      icon: FaVideo,
      color: '#EC4899',
      desc: isArabic ? 'Zoom، وجهاً لوجه' : 'Zoom, Face-to-face'
    }
  ];

  const containerWidth = 800;
  const containerHeight = 600;
  const topRowY = 80;
  const middleRowY = 280;
  const bottomRowY = 480;
  const itemSpacing = 240; // Increased spacing for 3 items

  return (
    <div
      ref={containerRef}
      style={{
        background: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.6)',
        borderRadius: 24,
        padding: '40px',
        width: '100%',
        maxWidth: containerWidth,
        position: 'relative',
        overflow: 'visible',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
        minHeight: containerHeight
      }}
    >
      <svg
        ref={svgRef}
        width={containerWidth}
        height={containerHeight}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none'
        }}
      >
        {/* Connection lines with proper paths and animations */}
        {[
          // Top to Middle connections
          { from: 0, to: 0, fromY: topRowY + 50, toY: middleRowY - 20, color: '#10B981' }, // Effective Learning -> Find Teachers (green)
          { from: 1, to: 0, fromY: topRowY + 50, toY: middleRowY - 20, color: '#EC4899' }, // Student Experience -> Find Teachers (pink)
          { from: 1, to: 1, fromY: topRowY + 50, toY: middleRowY - 20, color: '#FF7426' }, // Student Experience -> Book Sessions (orange)
          { from: 2, to: 1, fromY: topRowY + 50, toY: middleRowY - 20, color: '#3B82F6' }, // Unified Platform -> Book Sessions (light blue)
          { from: 2, to: 2, fromY: topRowY + 50, toY: middleRowY - 20, color: '#8B5CF6' }, // Unified Platform -> Track Progress (purple)
          // Bottom to Middle connections
          { from: 0, to: 1, fromY: bottomRowY - 30, toY: middleRowY + 20, color: '#8B5CF6' }, // Learning Platform -> Book Sessions (dark purple)
          { from: 0, to: 2, fromY: bottomRowY - 30, toY: middleRowY + 20, color: '#8B5CF6' }, // Learning Platform -> Track Progress (dark purple)
          { from: 1, to: 1, fromY: bottomRowY - 30, toY: middleRowY + 20, color: '#FF7426' }, // Session Types -> Book Sessions (orange)
          { from: 1, to: 2, fromY: bottomRowY - 30, toY: middleRowY + 20, color: '#EC4899' }  // Session Types -> Track Progress (pink)
        ].map((conn, idx) => {
          // Calculate positions for centered layout
          const centerX = containerWidth / 2;
          // Top row: 3 items centered
          const topItemSpacing = 200;
          const topStartX = centerX - topItemSpacing;
          // Middle row: 3 items centered
          const middleItemSpacing = 200;
          const middleStartX = centerX - middleItemSpacing;
          // Bottom row: 2 items centered
          const bottomItemSpacing = 280;
          const bottomStartX = centerX - bottomItemSpacing / 2;

          // Determine which row the 'from' item is in
          const fromIsTop = conn.fromY < middleRowY;
          const fromIsBottom = conn.fromY > middleRowY;

          let fromX;
          if (fromIsTop) {
            fromX = topStartX + conn.from * topItemSpacing;
          } else if (fromIsBottom) {
            fromX = bottomStartX + conn.from * bottomItemSpacing;
          } else {
            fromX = middleStartX + conn.from * middleItemSpacing;
          }

          const toX = middleStartX + conn.to * middleItemSpacing;
          const midY = (conn.fromY + conn.toY) / 2;
          const pathD = `M ${fromX} ${conn.fromY} Q ${(fromX + toX) / 2} ${midY} ${toX} ${conn.toY}`;

          return (
            <g key={idx}>
              {/* Base line (subtle) */}
              <line
                x1={fromX}
                y1={conn.fromY}
                x2={toX}
                y2={conn.toY}
                stroke={conn.color}
                strokeWidth="1"
                strokeDasharray="4,4"
                opacity={0.2}
              />
              {/* Animated curved line */}
              <motion.path
                d={pathD}
                fill="none"
                stroke={conn.color}
                strokeWidth="2"
                strokeDasharray="4,4"
                opacity={0.6}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={isInView ? {
                  pathLength: 1,
                  opacity: 0.6,
                  strokeDashoffset: [0, -8]
                } : {}}
                transition={{
                  pathLength: { duration: 1.5, delay: idx * 0.1, ease: "easeInOut" },
                  strokeDashoffset: { duration: 1, repeat: Infinity, ease: "linear" }
                }}
              />
              {/* Animated dot at end */}
              <motion.circle
                cx={toX}
                cy={conn.toY}
                r="5"
                fill={conn.color}
                initial={{ scale: 0, opacity: 0 }}
                animate={isInView ? {
                  scale: [0, 1.2, 1],
                  opacity: [0, 1, 1]
                } : {}}
                transition={{
                  duration: 0.5,
                  delay: 1 + idx * 0.1,
                  times: [0, 0.5, 1]
                }}
              />
            </g>
          );
        })}
      </svg>

      {/* Strategic Objectives - Top Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 60,
        position: 'relative',
        zIndex: 2
      }}>
        {strategicObjectives.map((obj, idx) => (
          <motion.div
            key={obj.id}
            initial={{ opacity: 0, y: -20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: idx * 0.1 }}
            onMouseEnter={() => setHoveredItem(obj.id)}
            onMouseLeave={() => setHoveredItem(null)}
            onClick={(e) => e.preventDefault()}
            style={{
              background: 'white',
              borderRadius: 12,
              padding: '16px 20px',
              width: 160,
              border: `1px solid ${hoveredItem === obj.id ? obj.color : '#E5E7EB'}`,
              boxShadow: hoveredItem === obj.id ? `0 4px 12px ${obj.color}20` : '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.3s ease',
              cursor: 'default'
            }}
          >
            <h4 style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#0F172A',
              marginBottom: 6,
              marginTop: 0
            }}>
              {obj.title}
            </h4>
            <p style={{
              fontSize: 11,
              color: '#64748B',
              margin: 0,
              lineHeight: 1.4
            }}>
              {obj.desc}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Core Processes - Middle Row (Diamond-shaped) */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 40,
        marginBottom: 60,
        position: 'relative',
        zIndex: 2
      }}>
        {coreProcesses.map((process, idx) => {
          const Icon1 = process.icon1;
          const Icon2 = process.icon2;
          return (
            <motion.div
              key={process.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.3 + idx * 0.1 }}
              onMouseEnter={() => setHoveredItem(process.id)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={(e) => e.preventDefault()}
              style={{
                background: process.color,
                borderRadius: 16,
                padding: '24px',
                width: 180,
                height: 140,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${hoveredItem === process.id ? '#FF7426' : 'transparent'}`,
                boxShadow: hoveredItem === process.id ? '0 8px 24px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.05)',
                transform: 'rotate(45deg)',
                transition: 'all 0.3s ease',
                cursor: 'default',
                position: 'relative'
              }}
            >
              <div style={{
                transform: 'rotate(-45deg)',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  position: 'absolute',
                  top: 8,
                  left: 8
                }}>
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: process.icon1Color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <Icon1 size={12} />
                  </div>
                </div>
                <h4 style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#0F172A',
                  margin: 0,
                  textAlign: 'center',
                  lineHeight: 1.3
                }}>
                  {process.title}
                </h4>
                <div style={{
                  display: 'flex',
                  gap: 8,
                  position: 'absolute',
                  bottom: 8,
                  right: 8
                }}>
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: process.icon2Color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 10,
                    fontWeight: 700
                  }}>
                    {process.icon2Text || <Icon2 size={12} />}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Foundational Capabilities - Bottom Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 40,
        position: 'relative',
        zIndex: 2
      }}>
        {capabilities.map((cap, idx) => {
          const Icon = cap.icon;
          return (
            <motion.div
              key={cap.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + idx * 0.1 }}
              onMouseEnter={() => setHoveredItem(cap.id)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={(e) => e.preventDefault()}
              style={{
                background: 'white',
                borderRadius: 12,
                padding: '16px',
                width: 140,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                border: `1px solid ${hoveredItem === cap.id ? cap.color : '#E5E7EB'}`,
                boxShadow: hoveredItem === cap.id ? `0 4px 12px ${cap.color}20` : '0 2px 8px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease',
                cursor: 'default'
              }}
            >
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: `${cap.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: cap.color
              }}>
                <Icon size={20} />
              </div>
              <h4 style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#0F172A',
                margin: 0,
                textAlign: 'center',
                marginBottom: cap.desc ? 4 : 0
              }}>
                {cap.title}
              </h4>
              {cap.desc && (
                <p style={{
                  fontSize: 10,
                  color: '#64748B',
                  margin: 0,
                  textAlign: 'center'
                }}>
                  {cap.desc}
                </p>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Animated Platform Network Visualization Component (kept for backward compatibility)
const PlatformNetworkVisualization = ({ isArabic }) => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-50px" });
  const [matchScore, setMatchScore] = useState(0);

  // Node positions for the network - more spacing between circles
  const nodes = [
    { id: 'center', x: 50, y: 50, size: 18, match: 95, label: 'Perfect Match', isCenter: true },
    { id: '1', x: 18, y: 22, size: 12, match: 68, label: 'Good Match' },
    { id: '2', x: 82, y: 22, size: 12, match: 70, label: 'Good Match' },
    { id: '3', x: 15, y: 78, size: 12, match: 75, label: 'Great Match' },
    { id: '4', x: 85, y: 78, size: 12, match: 62, label: 'Fair Match' },
  ];

  // Simplified connections - only from center to nodes
  const connections = [
    { from: 'center', to: '1' },
    { from: 'center', to: '2' },
    { from: 'center', to: '3' },
    { from: 'center', to: '4' },
  ];

  useEffect(() => {
    if (isInView) {
      // Animate match score
      const timer = setInterval(() => {
        setMatchScore(prev => {
          if (prev < 95) return prev + 1;
          return prev;
        });
      }, 30);
      return () => clearInterval(timer);
    }
  }, [isInView]);

  return (
    <div
      ref={containerRef}
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: 24,
        padding: 32,
        width: '100%',
        maxWidth: 500,
        position: 'relative',
        minHeight: 400
      }}
    >
      {/* Top Action Buttons - cleaner design */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 28,
        gap: 12
      }}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          style={{
            background: 'rgba(99, 102, 241, 0.2)',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            borderRadius: 10,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: '#A5B4FC',
            fontSize: 13,
            fontWeight: 600
          }}
        >
          <FaBrain size={16} />
          <span>{isArabic ? 'تحليل ذكي' : 'Smart Match'}</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
          style={{
            background: 'rgba(99, 102, 241, 0.2)',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            borderRadius: 10,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: '#A5B4FC',
            fontSize: 13,
            fontWeight: 600
          }}
        >
          <FaFilter size={16} />
          <span>{isArabic ? 'ترتيب تلقائي' : 'Auto Sort'}</span>
        </motion.div>
      </div>

      {/* Network Visualization */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: 450,
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          style={{ overflow: 'visible' }}
        >
          {/* Clean animated connection lines - lighter and more subtle */}
          {connections.map((conn, idx) => {
            const fromNode = nodes.find(n => n.id === conn.from);
            const toNode = nodes.find(n => n.id === conn.to);
            if (!fromNode || !toNode) return null;

            return (
              <g key={`${conn.from}-${conn.to}`}>
                {/* Subtle base line */}
                <line
                  x1={fromNode.x}
                  y1={fromNode.y}
                  x2={toNode.x}
                  y2={toNode.y}
                  stroke="rgba(99, 102, 241, 0.1)"
                  strokeWidth="0.8"
                  strokeDasharray="2,2"
                />
                {/* Animated flowing line - lighter */}
                <motion.line
                  x1={fromNode.x}
                  y1={fromNode.y}
                  x2={toNode.x}
                  y2={toNode.y}
                  stroke="rgba(255, 116, 38, 0.4)"
                  strokeWidth="1"
                  strokeDasharray="4,4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={isInView ? {
                    pathLength: 1,
                    opacity: 0.5,
                    strokeDashoffset: [0, -8]
                  } : {}}
                  transition={{
                    delay: 0.3 + idx * 0.15,
                    pathLength: { duration: 1, ease: "easeOut" },
                    opacity: { duration: 0.5 },
                    strokeDashoffset: {
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "linear"
                    }
                  }}
                />
              </g>
            );
          })}

          {/* Network nodes - clean and visible */}
          {nodes.map((node, idx) => (
            <g key={node.id}>
              {/* Subtle glow for center node only */}
              {node.isCenter && (
                <motion.circle
                  cx={node.x}
                  cy={node.y}
                  r={node.size + 2}
                  fill="none"
                  stroke="#FF7426"
                  strokeWidth="1"
                  opacity="0.3"
                  initial={{ scale: 1, opacity: 0 }}
                  animate={isInView ? {
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                  } : {}}
                  transition={{
                    delay: 0.8,
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              )}
              {/* Main circle - clean and bold with better spacing */}
              <motion.circle
                cx={node.x}
                cy={node.y}
                r={node.size}
                fill={node.isCenter
                  ? 'rgba(255, 116, 38, 0.35)'
                  : 'rgba(99, 102, 241, 0.3)'}
                stroke={node.isCenter ? '#FF7426' : '#6366F1'}
                strokeWidth={node.isCenter ? "2.5" : "2"}
                initial={{ scale: 0, opacity: 0 }}
                animate={isInView ? {
                  scale: 1,
                  opacity: 1
                } : {}}
                transition={{
                  delay: 0.4 + idx * 0.15,
                  duration: 0.6,
                  type: "spring",
                  stiffness: 150
                }}
              />
              {/* Content inside circle - clearly visible with white text for better contrast */}
              <motion.foreignObject
                x={node.x - node.size * 0.9}
                y={node.y - node.size * 0.9}
                width={node.size * 1.8}
                height={node.size * 1.8}
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.6 + idx * 0.15, duration: 0.5 }}
              >
                <div style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  textAlign: 'center',
                  pointerEvents: 'none'
                }}>
                  {node.isCenter ? (
                    <>
                      <FaChalkboardTeacher
                        size={node.size * 1.0}
                        style={{
                          marginBottom: 4,
                          color: 'white',
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                        }}
                      />
                      <motion.span
                        style={{
                          fontSize: `${node.size * 0.7}px`,
                          fontWeight: 900,
                          lineHeight: 1.2,
                          color: 'white',
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                        initial={{ scale: 0 }}
                        animate={isInView ? { scale: 1 } : {}}
                        transition={{ delay: 0.9, type: "spring", stiffness: 200 }}
                      >
                        {matchScore}%
                      </motion.span>
                    </>
                  ) : (
                    <>
                      <FaGraduationCap
                        size={node.size * 0.8}
                        style={{
                          marginBottom: 3,
                          color: 'white',
                          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))'
                        }}
                      />
                      <motion.span
                        style={{
                          fontSize: `${node.size * 0.55}px`,
                          fontWeight: 800,
                          lineHeight: 1.2,
                          color: 'white',
                          textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                        }}
                        initial={{ scale: 0 }}
                        animate={isInView ? { scale: 1 } : {}}
                        transition={{ delay: 0.7 + idx * 0.15, type: "spring", stiffness: 200 }}
                      >
                        {node.match}%
                      </motion.span>
                    </>
                  )}
                </div>
              </motion.foreignObject>
            </g>
          ))}
        </svg>
      </div>

      {/* Bottom Status Badge */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 1 }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12
        }}
      >
        <div style={{
          background: 'rgba(16, 185, 129, 0.2)',
          border: '1px solid rgba(16, 185, 129, 0.4)',
          borderRadius: 10,
          padding: '10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: '#34D399',
          fontSize: 13,
          fontWeight: 600
        }}>
          <FaCheck size={14} />
          <span>{isArabic ? 'مطابق' : 'Matched'}</span>
        </div>
        <div style={{
          background: 'rgba(99, 102, 241, 0.2)',
          border: '1px solid rgba(99, 102, 241, 0.4)',
          borderRadius: 10,
          padding: '10px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: '#A5B4FC',
          fontSize: 13,
          fontWeight: 600
        }}>
          <FaChartLine size={14} />
          <span>{isArabic ? 'ترتيب تلقائي' : 'Auto Ranking'}</span>
        </div>
      </motion.div>
    </div>
  );
};

export default function StripeHomepage({ lang = 'en', isStudent = true }) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [teacherRatings, setTeacherRatings] = useState({});
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevLangRef = useRef(lang);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const isArabic = lang === 'ar';

  // Handle language transition animation
  useEffect(() => {
    if (prevLangRef.current !== lang) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        prevLangRef.current = lang;
      }, 400);
      return () => clearTimeout(timer);
    }
    prevLangRef.current = lang;
  }, [lang]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch real teachers from database
  useEffect(() => {
    async function fetchTeachers() {
      try {
        setLoadingTeachers(true);
        const { data, error } = await supabase
          .from('Teachers')
          .select('full_name, email, profile_photo, subjects, grade_levels, hourly_rate, uuid_id, created_at')
          .not('full_name', 'is', null)
          .not('profile_photo', 'is', null)
          .limit(3)
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (data) {
          // Filter complete profiles
          const completeTeachers = data.filter(teacher => (
            teacher.full_name &&
            teacher.profile_photo &&
            Array.isArray(teacher.subjects) && teacher.subjects.length > 0 &&
            teacher.hourly_rate
          ));

          // Fetch ratings for teachers
          const teacherEmails = completeTeachers.map(t => t.email);
          if (teacherEmails.length > 0) {
            const ratingsData = await getMultipleTeacherRatings(teacherEmails);
            setTeacherRatings(ratingsData || {});
          }

          // Add fake teachers for now
          const fakeTeacher1 = {
            full_name: lang === 'ar' ? 'أحمد الخالدي' : 'Ahmed Al-Khaldi',
            email: 'fake-teacher-1@taealam.com',
            profile_photo: 'https://i.pravatar.cc/150?img=12',
            subjects: lang === 'ar' ? ['الرياضيات', 'الفيزياء'] : ['Mathematics', 'Physics'],
            grade_levels: lang === 'ar' ? ['الصف العاشر', 'الصف الحادي عشر'] : ['Grade 10', 'Grade 11'],
            hourly_rate: '8',
            uuid_id: 'fake-teacher-001',
            created_at: new Date().toISOString()
          };

          const fakeTeacher2 = {
            full_name: lang === 'ar' ? 'سارة محمد' : 'Sara Mohamed',
            email: 'fake-teacher-2@taealam.com',
            profile_photo: 'https://i.pravatar.cc/150?img=47',
            subjects: lang === 'ar' ? ['اللغة الإنجليزية', 'الأدب'] : ['English', 'Literature'],
            grade_levels: lang === 'ar' ? ['الصف السابع', 'الصف الثامن'] : ['Grade 7', 'Grade 8'],
            hourly_rate: '7',
            uuid_id: 'fake-teacher-002',
            created_at: new Date().toISOString()
          };

          const fakeTeacher3 = {
            full_name: lang === 'ar' ? 'محمد العلي' : 'Mohammed Al-Ali',
            email: 'fake-teacher-3@taealam.com',
            profile_photo: 'https://i.pravatar.cc/150?img=33',
            subjects: lang === 'ar' ? ['الكيمياء', 'الأحياء'] : ['Chemistry', 'Biology'],
            grade_levels: lang === 'ar' ? ['الصف التاسع', 'الصف العاشر'] : ['Grade 9', 'Grade 10'],
            hourly_rate: '9',
            uuid_id: 'fake-teacher-003',
            created_at: new Date().toISOString()
          };

          const fakeTeacher4 = {
            full_name: lang === 'ar' ? 'فاطمة حسن' : 'Fatima Hassan',
            email: 'fake-teacher-4@taealam.com',
            profile_photo: 'https://i.pravatar.cc/150?img=68',
            subjects: lang === 'ar' ? ['اللغة العربية', 'التاريخ'] : ['Arabic', 'History'],
            grade_levels: lang === 'ar' ? ['الصف الخامس', 'الصف السادس'] : ['Grade 5', 'Grade 6'],
            hourly_rate: '6',
            uuid_id: 'fake-teacher-004',
            created_at: new Date().toISOString()
          };

          setTeachers([...completeTeachers, fakeTeacher1, fakeTeacher2, fakeTeacher3, fakeTeacher4]);
        }
      } catch (err) {
        console.error('Error fetching teachers:', err);
        // Add fake teachers even if fetch fails
        const fakeTeacher1 = {
          full_name: lang === 'ar' ? 'أحمد الخالدي' : 'Ahmed Al-Khaldi',
          email: 'fake-teacher-1@taealam.com',
          profile_photo: 'https://i.pravatar.cc/150?img=12',
          subjects: lang === 'ar' ? ['الرياضيات', 'الفيزياء'] : ['Mathematics', 'Physics'],
          grade_levels: lang === 'ar' ? ['الصف العاشر', 'الصف الحادي عشر'] : ['Grade 10', 'Grade 11'],
          hourly_rate: '8',
          uuid_id: 'fake-teacher-001',
          created_at: new Date().toISOString()
        };

        const fakeTeacher2 = {
          full_name: lang === 'ar' ? 'سارة محمد' : 'Sara Mohamed',
          email: 'fake-teacher-2@taealam.com',
          profile_photo: 'https://i.pravatar.cc/150?img=47',
          subjects: lang === 'ar' ? ['اللغة الإنجليزية', 'الأدب'] : ['English', 'Literature'],
          grade_levels: lang === 'ar' ? ['الصف السابع', 'الصف الثامن'] : ['Grade 7', 'Grade 8'],
          hourly_rate: '7',
          uuid_id: 'fake-teacher-002',
          created_at: new Date().toISOString()
        };

        const fakeTeacher3 = {
          full_name: lang === 'ar' ? 'محمد العلي' : 'Mohammed Al-Ali',
          email: 'fake-teacher-3@taealam.com',
          profile_photo: 'https://i.pravatar.cc/150?img=33',
          subjects: lang === 'ar' ? ['الكيمياء', 'الأحياء'] : ['Chemistry', 'Biology'],
          grade_levels: lang === 'ar' ? ['الصف التاسع', 'الصف العاشر'] : ['Grade 9', 'Grade 10'],
          hourly_rate: '9',
          uuid_id: 'fake-teacher-003',
          created_at: new Date().toISOString()
        };

        const fakeTeacher4 = {
          full_name: lang === 'ar' ? 'فاطمة حسن' : 'Fatima Hassan',
          email: 'fake-teacher-4@taealam.com',
          profile_photo: 'https://i.pravatar.cc/150?img=68',
          subjects: lang === 'ar' ? ['اللغة العربية', 'التاريخ'] : ['Arabic', 'History'],
          grade_levels: lang === 'ar' ? ['الصف الخامس', 'الصف السادس'] : ['Grade 5', 'Grade 6'],
          hourly_rate: '6',
          uuid_id: 'fake-teacher-004',
          created_at: new Date().toISOString()
        };
        setTeachers([fakeTeacher1, fakeTeacher2, fakeTeacher3, fakeTeacher4]);
      } finally {
        setLoadingTeachers(false);
      }
    }

    fetchTeachers();
  }, [lang]);

  // Content based on language and user type
  const content = {
    en: {
      badge: isStudent ? "The Best Way to Connect" : "Teach and Earn",
      heroTitle: isStudent ? "The best way" : "Share your",
      heroTitleGradient: isStudent ? "to connect" : "knowledge",
      heroTitleGradient2: isStudent ? "with teachers" : "with students",
      heroSubtitle: isStudent
        ? "Find expert tutors, book sessions, and learn at your own pace. Everything you need in one place."
        : "Join our platform to teach students, manage your schedule, and grow your teaching business. Start earning today.",
      ctaPrimary: isStudent ? "Get Started" : "Start Teaching",
      ctaSecondary: isStudent ? "Find a Teacher" : "Learn More",
      trustedBy: isStudent ? "Trusted by learners across" : "Trusted by teachers across",
      stats: isStudent ? [
        { value: 500, suffix: '+', label: 'Active Teachers' },
        { value: 10, suffix: 'K+', label: 'Students Learning' },
        { value: 50, suffix: 'K+', label: 'Sessions Completed' },
        { value: 98, suffix: '%', label: 'Satisfaction Rate' }
      ] : [
        { value: 500, suffix: '+', label: 'Active Teachers' },
        { value: 10, suffix: 'K+', label: 'Students Taught' },
        { value: 50, suffix: 'K+', label: 'Sessions Delivered' },
        { value: 98, suffix: '%', label: 'Teacher Satisfaction' }
      ],
      features: isStudent ? [
        {
          icon: FaGraduationCap,
          title: 'Expert Teachers',
          desc: 'Connect with qualified educators passionate about helping you succeed.',
          color: '#FF7426'
        },
        {
          icon: FaCalendarAlt,
          title: 'Flexible Scheduling',
          desc: 'Book sessions that fit your schedule. Learn when it works for you.',
          color: '#10B981'
        },
        {
          icon: FaComments,
          title: 'Real-time Messaging',
          desc: 'Chat instantly with teachers through our built-in messaging system.',
          color: '#6366F1'
        },
        {
          icon: FaShieldAlt,
          title: 'Secure Platform',
          desc: 'Your privacy and data are protected with enterprise-grade security.',
          color: '#EC4899'
        }
      ] : [
        {
          icon: FaChalkboardTeacher,
          title: 'Reach More Students',
          desc: 'Connect with students looking for your expertise and grow your teaching business.',
          color: '#FF7426'
        },
        {
          icon: FaCalendarAlt,
          title: 'Flexible Schedule',
          desc: 'Set your own availability and teach when it works for you.',
          color: '#10B981'
        },
        {
          icon: FaComments,
          title: 'Direct Communication',
          desc: 'Chat with students directly through our built-in messaging system.',
          color: '#6366F1'
        },
        {
          icon: FaShieldAlt,
          title: 'Secure Payments',
          desc: 'Get paid securely and on time with our reliable payment system.',
          color: '#EC4899'
        }
      ],
      sectionTitle: isStudent
        ? "A fully integrated learning platform"
        : "A fully integrated teaching platform",
      sectionSubtitle: isStudent
        ? "Everything you need for effective education. Find teachers, book sessions, and achieve your goals on one powerful platform."
        : "Everything you need to teach effectively. Manage students, schedule sessions, and grow your teaching business on one powerful platform.",
      howItWorks: "How It Works",
      steps: isStudent ? [
        { num: '01', title: 'Create Account', desc: 'Sign up in seconds with your email or social account.' },
        { num: '02', title: 'Find Teachers', desc: 'Browse our network of qualified educators by subject, price, or rating.' },
        { num: '03', title: 'Book Sessions', desc: 'Choose available times and book lessons that fit your schedule.' },
        { num: '04', title: 'Start Learning', desc: 'Connect with your teacher and begin your educational journey.' }
      ] : [
        { num: '01', title: 'Create Your Profile', desc: 'Sign up and create your teacher profile with your expertise and qualifications.' },
        { num: '02', title: 'Set Your Schedule', desc: 'Define your availability and set your rates for different subjects.' },
        { num: '03', title: 'Get Booked', desc: 'Students will find and book sessions with you based on your profile.' },
        { num: '04', title: 'Start Teaching', desc: 'Conduct sessions, track progress, and build your teaching reputation.' }
      ],
      whyChoose: "A Partner You Can Trust",
      benefits: isStudent ? [
        { icon: FaUsers, title: 'One-on-One Learning', desc: 'Personalized attention from expert tutors tailored to your needs.' },
        { icon: FaClock, title: 'Learn Anytime', desc: 'Flexible scheduling that adapts to your busy lifestyle.' },
        { icon: FaGlobe, title: 'Any Subject', desc: 'From math to languages, find teachers for every subject.' },
        { icon: FaStar, title: 'Verified Reviews', desc: 'Read honest reviews from real students before booking.' }
      ] : [
        { icon: FaUsers, title: 'Grow Your Student Base', desc: 'Reach more students and expand your teaching business.' },
        { icon: FaClock, title: 'Work on Your Terms', desc: 'Set your own schedule and teach when it works for you.' },
        { icon: FaGlobe, title: 'Teach Any Subject', desc: 'From math to languages, teach what you know best.' },
        { icon: FaStar, title: 'Build Your Reputation', desc: 'Get reviews from students and build your teaching profile.' }
      ],
      ctaTitle: isStudent ? "Ready to get started?" : "Want to know how much you'll earn?",
      ctaSubtitle: isStudent
        ? "Create an account instantly to get started or contact us for more information."
        : "Use our calculator to see your earnings based on your hourly rate and session hours.",
      ctaButton: isStudent ? "Get Started Free" : "Calculate Earnings",
      footerDescription: isStudent
        ? "Taealam connects students with teachers for seamless lesson booking and effective learning."
        : "Taealam helps teachers connect with students, manage schedules, and grow their teaching business.",
      footerPlatform: "Platform",
      footerResources: "Resources",
      footerLegal: "Legal",
      footerLinks: isStudent ? {
        platform: [
          { label: 'How it Works', path: '#how-it-works-section', isAnchor: true },
          { label: 'Find a Teacher', path: '/signin', isAnchor: false },
          { label: 'Become a Teacher', path: 'http://teacher.localhost:3000/', isAnchor: false, isExternal: true }
        ],
        resources: [
          { label: 'Help Center', path: '/help-center', isAnchor: false },
          { label: 'FAQ', path: '/faq', isAnchor: false }
        ],
        legal: [
          { label: 'Privacy Policy', path: '/privacy', isAnchor: false },
          { label: 'User Agreement', path: '/terms', isAnchor: false }
        ]
      } : {
        platform: [
          { label: 'How it Works', path: '#how-it-works-section', isAnchor: true },
          { label: 'Get Started', path: '/signup', isAnchor: false },
          { label: 'Pricing', path: '/pricing', isAnchor: false }
        ],
        resources: [
          { label: 'Help Center', path: '/help-center', isAnchor: false },
          { label: 'FAQ', path: '/faq', isAnchor: false }
        ],
        legal: [
          { label: 'Privacy Policy', path: '/privacy', isAnchor: false },
          { label: 'User Agreement', path: '/terms', isAnchor: false }
        ]
      },
      footerCopyright: "All rights reserved."
    },
    ar: {
      badge: isStudent ? "أفضل طريقة للتواصل" : "علّم واكسب",
      heroTitle: isStudent ? "أفضل طريقة" : "شارك",
      heroTitleGradient: isStudent ? "للتواصل" : "معرفتك",
      heroTitleGradient2: isStudent ? "مع المعلمين" : "مع الطلاب",
      heroSubtitle: isStudent
        ? "ابحث عن معلمين خبراء، احجز جلسات، وتعلم بالسرعة التي تناسبك. كل ما تحتاجه في مكان واحد."
        : "انضم إلى منصتنا لتعليم الطلاب، وإدارة جدولك، وتنمية أعمالك التعليمية. ابدأ في الكسب اليوم.",
      ctaPrimary: isStudent ? "ابدأ الآن" : "ابدأ التدريس",
      ctaSecondary: isStudent ? "ابحث عن معلم" : "اعرف المزيد",
      trustedBy: isStudent ? "موثوق من المتعلمين في" : "موثوق من المعلمين في",
      stats: isStudent ? [
        { value: 500, suffix: '+', label: 'معلم نشط' },
        { value: 10, suffix: 'K+', label: 'طالب يتعلم' },
        { value: 50, suffix: 'K+', label: 'جلسة مكتملة' },
        { value: 98, suffix: '%', label: 'نسبة رضا' }
      ] : [
        { value: 500, suffix: '+', label: 'معلم نشط' },
        { value: 10, suffix: 'K+', label: 'طالب تم تعليمه' },
        { value: 50, suffix: 'K+', label: 'جلسة تم تقديمها' },
        { value: 98, suffix: '%', label: 'رضا المعلمين' }
      ],
      features: isStudent ? [
        {
          icon: FaGraduationCap,
          title: 'معلمون خبراء',
          desc: 'تواصل مع معلمين مؤهلين متحمسين لمساعدتك على النجاح.',
          color: '#FF7426'
        },
        {
          icon: FaCalendarAlt,
          title: 'جدولة مرنة',
          desc: 'احجز جلسات تناسب جدولك. تعلم عندما يناسبك.',
          color: '#10B981'
        },
        {
          icon: FaComments,
          title: 'رسائل فورية',
          desc: 'تحدث مباشرة مع المعلمين من خلال نظام المراسلة المدمج.',
          color: '#6366F1'
        },
        {
          icon: FaShieldAlt,
          title: 'منصة آمنة',
          desc: 'خصوصيتك وبياناتك محمية بأمان على مستوى المؤسسات.',
          color: '#EC4899'
        }
      ] : [
        {
          icon: FaChalkboardTeacher,
          title: 'الوصول إلى المزيد من الطلاب',
          desc: 'تواصل مع الطلاب الذين يبحثون عن خبرتك ونمّ أعمالك التعليمية.',
          color: '#FF7426'
        },
        {
          icon: FaCalendarAlt,
          title: 'جدولة مرنة',
          desc: 'حدد توفرك الخاص وعلّم عندما يناسبك.',
          color: '#10B981'
        },
        {
          icon: FaComments,
          title: 'تواصل مباشر',
          desc: 'تحدث مع الطلاب مباشرة من خلال نظام المراسلة المدمج.',
          color: '#6366F1'
        },
        {
          icon: FaShieldAlt,
          title: 'مدفوعات آمنة',
          desc: 'احصل على أموالك بأمان وفي الوقت المحدد مع نظام الدفع الموثوق.',
          color: '#EC4899'
        }
      ],
      sectionTitle: isStudent
        ? "منصة تعليمية متكاملة"
        : "منصة تدريس متكاملة",
      sectionSubtitle: isStudent
        ? "كل ما تحتاجه للتعليم الفعال. ابحث عن المعلمين، احجز الجلسات، وحقق أهدافك على منصة واحدة قوية."
        : "كل ما تحتاجه للتدريس الفعال. إدارة الطلاب، وجدولة الجلسات، وتنمية أعمالك التعليمية على منصة واحدة قوية.",
      howItWorks: "كيف يعمل",
      steps: isStudent ? [
        { num: '01', title: 'إنشاء حساب', desc: 'سجل في ثوانٍ باستخدام بريدك الإلكتروني أو حسابك الاجتماعي.' },
        { num: '02', title: 'ابحث عن معلمين', desc: 'تصفح شبكتنا من المعلمين المؤهلين حسب المادة أو السعر أو التقييم.' },
        { num: '03', title: 'احجز جلسات', desc: 'اختر الأوقات المتاحة واحجز دروسًا تناسب جدولك.' },
        { num: '04', title: 'ابدأ التعلم', desc: 'تواصل مع معلمك وابدأ رحلتك التعليمية.' }
      ] : [
        { num: '01', title: 'أنشئ ملفك الشخصي', desc: 'سجل وأنشئ ملفك الشخصي كمعلم مع خبرتك ومؤهلاتك.' },
        { num: '02', title: 'حدد جدولك', desc: 'حدد توفرك وحدد أسعارك للمواد المختلفة.' },
        { num: '03', title: 'احصل على الحجوزات', desc: 'سيجد الطلاب ويحجزون الجلسات معك بناءً على ملفك الشخصي.' },
        { num: '04', title: 'ابدأ التدريس', desc: 'قم بإجراء الجلسات، وتتبع التقدم، وبناء سمعتك التعليمية.' }
      ],
      whyChoose: "شريك يمكنك الثقة به",
      benefits: isStudent ? [
        { icon: FaUsers, title: 'تعلم فردي', desc: 'اهتمام شخصي من معلمين خبراء مصمم لاحتياجاتك.' },
        { icon: FaClock, title: 'تعلم في أي وقت', desc: 'جدولة مرنة تتكيف مع نمط حياتك المزدحم.' },
        { icon: FaGlobe, title: 'أي مادة', desc: 'من الرياضيات إلى اللغات، ابحث عن معلمين لكل مادة.' },
        { icon: FaStar, title: 'تقييمات موثوقة', desc: 'اقرأ تقييمات صادقة من طلاب حقيقيين قبل الحجز.' }
      ] : [
        { icon: FaUsers, title: 'نمّ قاعدة طلابك', desc: 'الوصول إلى المزيد من الطلاب وتوسيع أعمالك التعليمية.' },
        { icon: FaClock, title: 'اعمل بشروطك', desc: 'حدد جدولك الخاص وعلّم عندما يناسبك.' },
        { icon: FaGlobe, title: 'علّم أي مادة', desc: 'من الرياضيات إلى اللغات، علّم ما تعرفه بشكل أفضل.' },
        { icon: FaStar, title: 'ابنِ سمعتك', desc: 'احصل على تقييمات من الطلاب وابنِ ملفك التعليمي.' }
      ],
      ctaTitle: isStudent ? "مستعد للبدء؟" : "تريد معرفة كم ستربح؟",
      ctaSubtitle: isStudent
        ? "أنشئ حسابًا فورًا للبدء أو تواصل معنا لمزيد من المعلومات."
        : "استخدم حاسبتنا لمعرفة أرباحك بناءً على سعرك بالساعة وساعات الجلسات.",
      ctaButton: isStudent ? "ابدأ مجانًا" : "احسب أرباحك",
      footerDescription: isStudent
        ? "تعلّم هي منصة تربط الطلاب بالمعلمين لتسهيل حجز الدروس والتواصل بسهولة."
        : "تعلّم تساعد المعلمين على التواصل مع الطلاب، وإدارة الجداول، وتنمية أعمالهم التعليمية.",
      footerPlatform: "المنصة",
      footerResources: "الموارد",
      footerLegal: "قانوني",
      footerLinks: isStudent ? {
        platform: [
          { label: 'كيف تعمل', path: '#how-it-works-section', isAnchor: true },
          { label: 'ابحث عن معلم', path: '/signin', isAnchor: false },
          { label: 'كن معلماً', path: 'http://teacher.localhost:3000/', isAnchor: false, isExternal: true }
        ],
        resources: [
          { label: 'مركز المساعدة', path: '/help-center', isAnchor: false },
          { label: 'الأسئلة الشائعة', path: '/faq', isAnchor: false }
        ],
        legal: [
          { label: 'سياسة الخصوصية', path: '/privacy', isAnchor: false },
          { label: 'اتفاقية المستخدم', path: '/terms', isAnchor: false }
        ]
      } : {
        platform: [
          { label: 'كيف تعمل', path: '#how-it-works-section', isAnchor: true },
          { label: 'ابدأ الآن', path: '/signup', isAnchor: false },
          { label: 'الأسعار', path: '/pricing', isAnchor: false }
        ],
        resources: [
          { label: 'مركز المساعدة', path: '/help-center', isAnchor: false },
          { label: 'الأسئلة الشائعة', path: '/faq', isAnchor: false }
        ],
        legal: [
          { label: 'سياسة الخصوصية', path: '/privacy', isAnchor: false },
          { label: 'اتفاقية المستخدم', path: '/terms', isAnchor: false }
        ]
      },
      footerCopyright: "جميع الحقوق محفوظة."
    }
  };

  const t = content[lang];

  // FAQ data for structured data
  const faqs = isArabic ? [
    {
      question: "كيف تعمل منصة تعلّم؟",
      answer: "تعلّم تربط الطلاب بالمعلمين المؤهلين. ببساطة أنشئ حساباً، تصفح ملفات المعلمين، واحجز جلسة تناسب جدولك الزمني."
    },
    {
      question: "كم تكلفة الدروس على تعلّم؟",
      answer: "تختلف أسعار الدروس حسب المادة وخبرة المعلم، وتتراوح عادة من 5 إلى 15 دينار بحريني في الساعة."
    },
    {
      question: "هل معلمو تعلّم مؤهلون؟",
      answer: "نعم، يتم مراجعة والتحقق من جميع المعلمين بعناية. يمكنك عرض مؤهلاتهم والمواد التي يدرسونها ومراجعات الطلاب قبل الحجز."
    }
  ] : [
    {
      question: "How does Taealam work?",
      answer: "Taealam connects students with qualified tutors. Simply create an account, browse teacher profiles, and book a session that fits your schedule."
    },
    {
      question: "How much does tutoring cost on Taealam?",
      answer: "Tutoring rates vary by subject and teacher experience, typically ranging from 5-15 BHD per hour."
    },
    {
      question: "Are Taealam teachers qualified?",
      answer: "Yes, all teachers are carefully reviewed and verified. You can view their qualifications, subjects, and student reviews before booking."
    }
  ];

  // Generate structured data for SEO
  const structuredData = [
    generateOrganizationSchema(),
    generateWebsiteSchema(),
    generateFAQSchema(faqs)
  ];

  // SEO data
  const seoData = isArabic ? {
    title: "تواصل مع معلمين خبراء في البحرين",
    description: "ابحث عن معلمين مؤهلين، احجز دروساً عبر الإنترنت، وحقق أهدافك التعليمية. تعلّم تربط الطلاب بالمعلمين الخبراء في جميع أنحاء البحرين للتعليم الشخصي.",
    keywords: "دروس خصوصية البحرين, معلمين أونلاين, دروس خاصة, منصة تعليمية, تعلم عبر الإنترنت, معلمين البحرين"
  } : {
    title: "Connect with Expert Tutors in Bahrain",
    description: "Find qualified tutors, book online sessions, and achieve your learning goals. Taealam connects students with expert teachers across Bahrain for personalized education.",
    keywords: "tutoring Bahrain, online tutors, private lessons, education platform, learn online, Bahrain teachers"
  };

  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{
        fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
        overflowX: 'hidden'
      }}
    >
      {/* SEO Meta Tags */}
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl="/"
        alternateUrls={{ en: '/', ar: '/' }}
        lang={lang}
        structuredData={structuredData}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        .stripe-gradient-text {
          background: linear-gradient(135deg, #FF7426 0%, #FF9854 50%, #FF7426 100%);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 5s ease infinite;
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .stripe-btn-primary {
          background: linear-gradient(135deg, #FF7426 0%, #FF9854 100%);
          border: none;
          color: white;
          padding: 14px 28px;
          border-radius: 100px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 14px rgba(255, 116, 38, 0.4);
        }
        
        .stripe-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(255, 116, 38, 0.5);
        }
        
        .stripe-btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 14px 28px;
          border-radius: 100px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .stripe-btn-secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.4);
        }

        /* Mobile-specific button styles */
        @media (max-width: 768px) {
          .stripe-btn-primary,
          .stripe-btn-secondary {
            padding: 14px 24px;
            font-size: 14px;
            min-height: 48px;
          }

          .stripe-btn-primary:hover,
          .stripe-btn-secondary:hover {
            transform: none;
          }
        }

        .stripe-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 32px;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .stripe-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.15);
          transform: translateY(-4px);
        }
        
        .feature-icon-wrap {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          transition: transform 0.3s ease;
        }
        
        .stripe-card:hover .feature-icon-wrap {
          transform: scale(1.1);
        }
        
        .dashboard-preview {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 50px 100px rgba(0, 0, 0, 0.3);
        }
        
        .dashboard-preview::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.3) 100%);
          z-index: 1;
          pointer-events: none;
        }
        
        .glow-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          z-index: 0;
        }
        
        
        @media (max-width: 768px) {
          .hero-buttons {
            flex-direction: column;
            width: 100%;
          }
          .hero-buttons button {
            width: 100%;
            justify-content: center;
          }
          .features-grid {
            grid-template-columns: 1fr !important;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .steps-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <AnimatePresence mode="wait">
        <motion.div
          key={lang}
          initial={{ opacity: 0, x: isArabic ? 30 : -30 }}
          animate={{ opacity: isTransitioning ? 0.7 : 1, x: 0 }}
          exit={{ opacity: 0, x: isArabic ? -30 : 30 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          style={{ width: '100%' }}
        >
          {/* Hero Section - Stripe Style: Text Left, Image Right */}
          <section
            ref={heroRef}
            style={{
              minHeight: '100vh',
              height: isMobile ? '100vh' : '110vh',
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              padding: isMobile ? '100px 16px 60px' : '160px 40px 120px'
            }}
          >
            {/* Background Image */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${isStudent ? studentImage : teacherImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                transform: (isArabic && isStudent) || (!isStudent && !isArabic) ? 'scaleX(-1)' : 'none',
                WebkitTransform: (isArabic && isStudent) || (!isStudent && !isArabic) ? 'scaleX(-1)' : 'none',
                msTransform: (isArabic && isStudent) || (!isStudent && !isArabic) ? 'scaleX(-1)' : 'none',
                OTransform: (isArabic && isStudent) || (!isStudent && !isArabic) ? 'scaleX(-1)' : 'none',
                zIndex: 0
              }}
            />

            {/* Overlay for better text readability */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.4)',
              zIndex: 1
            }} />

            <div style={{
              maxWidth: 1200,
              margin: '0 auto',
              width: '100%',
              position: 'relative',
              zIndex: 2
            }}>
              {/* Text Content */}
              <motion.div
                style={{
                  y: heroY,
                  opacity: heroOpacity
                }}
                initial={{ opacity: 0, x: isArabic ? -30 : -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Badge */}
                <div style={{
                  marginBottom: 24
                }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(255, 116, 38, 0.1)',
                    border: '1px solid rgba(255, 116, 38, 0.2)',
                    padding: '8px 20px',
                    borderRadius: 100,
                    color: '#FF7426',
                    fontSize: 14,
                    fontWeight: 600
                  }}>
                    <FaStar size={16} />
                    {t.badge}
                  </div>
                </div>

                {/* Hero Title - Single H1 for SEO, displayed as 3 lines */}
                <h1 style={{
                  fontSize: isMobile ? 'clamp(2rem, 8vw, 3rem)' : 'clamp(3rem, 6vw, 5rem)',
                  fontWeight: 800,
                  textAlign: isMobile ? 'center' : (isArabic ? 'right' : 'left'),
                  color: '#F8FAFC',
                  marginBottom: 24,
                  lineHeight: 1.1
                }}>
                  {t.heroTitle}<br />
                  <span className="stripe-gradient-text">{t.heroTitleGradient}</span><br />
                  <span className="stripe-gradient-text">{t.heroTitleGradient2}</span>
                </h1>

                {/* Subtitle */}
                <p style={{
                  fontSize: isMobile ? 16 : 18,
                  color: '#94A3B8',
                  textAlign: isMobile ? 'center' : (isArabic ? 'right' : 'left'),
                  maxWidth: 600,
                  marginBottom: 40,
                  lineHeight: 1.7
                }}>
                  {t.heroSubtitle}
                </p>

                {/* CTA Buttons */}
                <div className="hero-buttons" style={{
                  display: 'flex',
                  gap: isMobile ? 12 : 16,
                  justifyContent: isMobile ? 'center' : (isArabic ? 'flex-start' : 'flex-start'),
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'center' : 'flex-start',
                  width: '100%',
                  maxWidth: isMobile ? '280px' : '600px',
                  margin: isMobile ? '0 auto' : '0'
                }}>
                  <button
                    className="stripe-btn-primary"
                    onClick={() => navigate('/signup')}
                    style={{
                      width: isMobile ? '100%' : 'auto',
                      minWidth: isMobile ? 'auto' : '180px',
                      maxWidth: isMobile ? '280px' : 'none',
                      justifyContent: 'center'
                    }}
                  >
                    {t.ctaPrimary}
                    <FaArrowRight size={14} style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />
                  </button>
                  <button
                    className="stripe-btn-secondary"
                    onClick={() => navigate('/signup')}
                    style={{
                      width: isMobile ? '100%' : 'auto',
                      minWidth: isMobile ? 'auto' : '180px',
                      maxWidth: isMobile ? '280px' : 'none',
                      justifyContent: 'center'
                    }}
                  >
                    {t.ctaSecondary}
                    <FaArrowRight size={14} style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />
                  </button>
                </div>
              </motion.div>

            </div>
          </section>

          {/* Spacer before Why Choose Section */}
          <div style={{ height: isMobile ? 60 : 100 }} />

          {/* Why Choose Section - Redesigned with Website Theme */}
          <section id="why-choose-section" style={{
            background: '#F8FAFC',
            padding: isMobile ? '80px 20px' : '120px 40px',
            position: 'relative'
          }}>
            <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
              <FadeInSection>
                <div style={{ textAlign: 'center', marginBottom: 64 }}>
                  <motion.h2
                    style={{
                      fontSize: isMobile ? 32 : 42,
                      fontWeight: 800,
                      color: '#0F172A',
                      marginBottom: 12,
                      lineHeight: 1.2
                    }}
                  >
                    {isStudent ? (
                      isArabic ? (
                        <>
                          <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            لماذا تختار
                          </motion.span>{' '}
                          <motion.span
                            style={{ color: '#FF7426' }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            تعلّم؟
                          </motion.span>
                        </>
                      ) : (
                        <>
                          <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            Why Choose
                          </motion.span>{' '}
                          <motion.span
                            style={{ color: '#FF7426' }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            Taealam?
                          </motion.span>
                        </>
                      )
                    ) : (
                      isArabic ? (
                        <>
                          <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            لماذا يثق المعلمون
                          </motion.span>{' '}
                          <motion.span
                            style={{ color: '#FF7426' }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            بتعلّم؟
                          </motion.span>
                        </>
                      ) : (
                        <>
                          <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            Why Teachers Trust
                          </motion.span>{' '}
                          <motion.span
                            style={{ color: '#FF7426' }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            Taealam
                          </motion.span>
                        </>
                      )
                    )}
                  </motion.h2>
                </div>
              </FadeInSection>

              <div
                className="features-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: 32,
                  maxWidth: 1100,
                  margin: '0 auto'
                }}
              >
                {t.benefits.slice(0, 3).map((benefit, idx) => (
                  <FadeInSection key={idx} delay={idx * 0.15}>
                    <motion.div
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.08) 0%, rgba(255, 116, 38, 0.03) 100%)',
                        borderRadius: 20,
                        padding: '40px 32px',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        border: '1px solid rgba(255, 116, 38, 0.1)'
                      }}
                      whileHover={{
                        y: -8,
                        scale: 1.02,
                        boxShadow: '0 12px 32px rgba(255, 116, 38, 0.15)',
                        borderColor: 'rgba(255, 116, 38, 0.2)',
                        transition: { duration: 0.3 }
                      }}
                      initial={{ opacity: 0, y: 30, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        delay: idx * 0.2 + 0.3,
                        duration: 0.6,
                        type: "spring",
                        stiffness: 100
                      }}
                    >
                      {/* Icon with animation */}
                      <motion.div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 16,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 24,
                          color: '#FF7426'
                        }}
                        whileHover={{
                          scale: 1.1,
                          rotate: 5,
                          transition: { duration: 0.3 }
                        }}
                      >
                        <benefit.icon size={28} />
                      </motion.div>

                      <h3 style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: '#0F172A',
                        marginBottom: 12,
                        marginTop: 0
                      }}>
                        {benefit.title}
                      </h3>
                      <p style={{
                        fontSize: 16,
                        color: '#64748B',
                        lineHeight: 1.6,
                        margin: 0
                      }}>
                        {benefit.desc}
                      </p>
                    </motion.div>
                  </FadeInSection>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works Section - Compact Style */}
          <HowItWorksSection lang={lang} isArabic={isArabic} isMobile={isMobile} navigate={navigate} t={t} isStudent={isStudent} />

          {/* Our Teachers Section - Only for Students */}
          {isStudent && (
            <section id="our-teachers-section" style={{
              background: '#F8FAFC',
              padding: isMobile ? '80px 20px' : '100px 40px'
            }}>
              <OurTeachersSection lang={lang} />
            </section>
          )}

          {/* Teacher Features Section - Only for Teachers */}
          {!isStudent && (
            <section id="teacher-features-section" style={{
              background: '#F8FAFC',
              padding: isMobile ? '80px 20px' : '100px 40px'
            }}>
              <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <FadeInSection>
                  <div style={{ textAlign: 'center', marginBottom: 64 }}>
                    <motion.h2
                      style={{
                        fontSize: isMobile ? 32 : 42,
                        fontWeight: 800,
                        color: '#0F172A',
                        marginBottom: 12,
                        lineHeight: 1.2
                      }}
                    >
                      {isArabic ? (
                        <>
                          <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            ميزات تعلّم
                          </motion.span>{' '}
                          <motion.span
                            style={{ color: '#FF7426' }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            للمعلمين
                          </motion.span>
                        </>
                      ) : (
                        <>
                          <motion.span
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                          >
                            Taealam Features
                          </motion.span>{' '}
                          <motion.span
                            style={{ color: '#FF7426' }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          >
                            for Teachers
                          </motion.span>
                        </>
                      )}
                    </motion.h2>
                    <p style={{
                      fontSize: 18,
                      color: '#64748B',
                      maxWidth: 700,
                      margin: '0 auto',
                      marginTop: 16
                    }}>
                      {isArabic
                        ? 'أدوات قوية مدعومة بالذكاء الاصطناعي لمساعدتك في التدريس الفعال'
                        : 'Powerful AI-powered tools to help you teach effectively'}
                    </p>
                  </div>
                </FadeInSection>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: 32,
                    maxWidth: 1100,
                    margin: '0 auto'
                  }}
                >
                  {/* Feature 1: AI Summary & Quiz */}
                  <FadeInSection delay={0.1}>
                    <motion.div
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.08) 0%, rgba(255, 116, 38, 0.03) 100%)',
                        borderRadius: 20,
                        padding: '40px 32px',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        border: '1px solid rgba(255, 116, 38, 0.1)'
                      }}
                      whileHover={{
                        y: -8,
                        scale: 1.02,
                        boxShadow: '0 12px 32px rgba(255, 116, 38, 0.15)',
                        borderColor: 'rgba(255, 116, 38, 0.2)',
                        transition: { duration: 0.3 }
                      }}
                      initial={{ opacity: 0, y: 30, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        delay: 0.1,
                        duration: 0.6,
                        type: "spring",
                        stiffness: 100
                      }}
                    >
                      <motion.div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 16,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 24,
                          color: '#FF7426'
                        }}
                        whileHover={{
                          scale: 1.1,
                          rotate: 5,
                          transition: { duration: 0.3 }
                        }}
                      >
                        <FaBrain size={28} />
                      </motion.div>

                      <h3 style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: '#0F172A',
                        marginBottom: 12,
                        marginTop: 0
                      }}>
                        {isArabic ? 'ملخص AI والاختبارات' : 'AI Summary & Quizzes'}
                      </h3>
                      <p style={{
                        fontSize: 16,
                        color: '#64748B',
                        lineHeight: 1.6,
                        margin: 0
                      }}>
                        {isArabic
                          ? 'يقوم الذكاء الاصطناعي بإنشاء ملخص كامل للطلاب من التسجيل وإنشاء اختبارات تفاعلية لتعزيز التعلم.'
                          : 'AI automatically generates comprehensive summaries for students from recordings and creates interactive quizzes to reinforce learning.'}
                      </p>
                    </motion.div>
                  </FadeInSection>

                  {/* Feature 2: Automatic Recording */}
                  <FadeInSection delay={0.2}>
                    <motion.div
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.08) 0%, rgba(255, 116, 38, 0.03) 100%)',
                        borderRadius: 20,
                        padding: '40px 32px',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        border: '1px solid rgba(255, 116, 38, 0.1)'
                      }}
                      whileHover={{
                        y: -8,
                        scale: 1.02,
                        boxShadow: '0 12px 32px rgba(255, 116, 38, 0.15)',
                        borderColor: 'rgba(255, 116, 38, 0.2)',
                        transition: { duration: 0.3 }
                      }}
                      initial={{ opacity: 0, y: 30, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        delay: 0.2,
                        duration: 0.6,
                        type: "spring",
                        stiffness: 100
                      }}
                    >
                      <motion.div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 16,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 24,
                          color: '#FF7426'
                        }}
                        whileHover={{
                          scale: 1.1,
                          rotate: 5,
                          transition: { duration: 0.3 }
                        }}
                      >
                        <FaVideo size={28} />
                      </motion.div>

                      <h3 style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: '#0F172A',
                        marginBottom: 12,
                        marginTop: 0
                      }}>
                        {isArabic ? 'التسجيل التلقائي' : 'Automatic Recording'}
                      </h3>
                      <p style={{
                        fontSize: 16,
                        color: '#64748B',
                        lineHeight: 1.6,
                        margin: 0
                      }}>
                        {isArabic
                          ? 'يتم تسجيل جميع الجلسات تلقائياً ونسخها، مما يتيح للطلاب مراجعة المحتوى في أي وقت.'
                          : 'All sessions are automatically recorded and transcribed, allowing students to review content anytime.'}
                      </p>
                    </motion.div>
                  </FadeInSection>

                  {/* Feature 3: Progress Tracking */}
                  <FadeInSection delay={0.3}>
                    <motion.div
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 116, 38, 0.08) 0%, rgba(255, 116, 38, 0.03) 100%)',
                        borderRadius: 20,
                        padding: '40px 32px',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        border: '1px solid rgba(255, 116, 38, 0.1)'
                      }}
                      whileHover={{
                        y: -8,
                        scale: 1.02,
                        boxShadow: '0 12px 32px rgba(255, 116, 38, 0.15)',
                        borderColor: 'rgba(255, 116, 38, 0.2)',
                        transition: { duration: 0.3 }
                      }}
                      initial={{ opacity: 0, y: 30, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        delay: 0.3,
                        duration: 0.6,
                        type: "spring",
                        stiffness: 100
                      }}
                    >
                      <motion.div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: 16,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 24,
                          color: '#FF7426'
                        }}
                        whileHover={{
                          scale: 1.1,
                          rotate: 5,
                          transition: { duration: 0.3 }
                        }}
                      >
                        <FaChartLine size={28} />
                      </motion.div>

                      <h3 style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: '#0F172A',
                        marginBottom: 12,
                        marginTop: 0
                      }}>
                        {isArabic ? 'تتبع التقدم' : 'Progress Tracking'}
                      </h3>
                      <p style={{
                        fontSize: 16,
                        color: '#64748B',
                        lineHeight: 1.6,
                        margin: 0
                      }}>
                        {isArabic
                          ? 'راقب تقدم الطلاب واحصل على رؤى قيمة حول أدائهم ومشاركتهم في الجلسات.'
                          : 'Track student progress and get valuable insights into their performance and engagement in sessions.'}
                      </p>
                    </motion.div>
                  </FadeInSection>
                </div>
              </div>
            </section>
          )}

          {/* Contact Us Section */}
          <section id="contact-us-section" style={{
            background: '#F8FAFC',
            padding: isMobile ? '50px 20px' : '70px 40px'
          }}>
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
              <FadeInSection>
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                  <h2 style={{
                    fontSize: isMobile ? 28 : 36,
                    fontWeight: 800,
                    color: '#0F172A',
                    marginBottom: 12
                  }}>
                    {isArabic ? 'تواصل معنا' : 'Contact Us'}
                  </h2>
                  <p style={{
                    fontSize: 16,
                    color: '#64748B',
                    maxWidth: 500,
                    margin: '0 auto',
                    lineHeight: 1.6
                  }}>
                    {isArabic
                      ? 'نحن هنا للمساعدة. تواصل معنا لأي استفسارات أو أسئلة.'
                      : 'We\'re here to help. Get in touch with us for any inquiries or questions.'}
                  </p>
                </div>
              </FadeInSection>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? 30 : 40,
                alignItems: 'flex-start'
              }}>
                {/* Left: Contact Form */}
                <FadeInSection delay={0.2}>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: 16,
                    padding: isMobile ? 20 : 28,
                    border: '1px solid #E2E8F0'
                  }}>
                    <h3 style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#0F172A',
                      marginBottom: 20
                    }}>
                      {isArabic ? 'أرسل لنا رسالة' : 'Send us a Message'}
                    </h3>

                    <form onSubmit={(e) => {
                      e.preventDefault();
                      alert(isArabic ? 'شكراً لك! سنتواصل معك قريباً.' : 'Thank you! We\'ll get back to you soon.');
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Name */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#0F172A',
                            marginBottom: 6
                          }}>
                            {isArabic ? 'الاسم' : 'Name'}
                          </label>
                          <input
                            type="text"
                            required
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              fontSize: 14,
                              border: '1px solid #E2E8F0',
                              borderRadius: 8,
                              background: '#FFFFFF',
                              color: '#0F172A',
                              fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                              transition: 'border-color 0.3s ease',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                          />
                        </div>

                        {/* Email */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#0F172A',
                            marginBottom: 6
                          }}>
                            {isArabic ? 'البريد الإلكتروني' : 'Email'}
                          </label>
                          <input
                            type="email"
                            required
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              fontSize: 14,
                              border: '1px solid #E2E8F0',
                              borderRadius: 8,
                              background: '#FFFFFF',
                              color: '#0F172A',
                              fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                              transition: 'border-color 0.3s ease',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                          />
                        </div>

                        {/* Message */}
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#0F172A',
                            marginBottom: 6
                          }}>
                            {isArabic ? 'الرسالة' : 'Message'}
                          </label>
                          <textarea
                            required
                            rows={4}
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              fontSize: 14,
                              border: '1px solid #E2E8F0',
                              borderRadius: 8,
                              background: '#FFFFFF',
                              color: '#0F172A',
                              fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                              resize: 'vertical',
                              transition: 'border-color 0.3s ease',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#FF7426'}
                            onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                          />
                        </div>

                        {/* Submit Button */}
                        <button
                          type="submit"
                          style={{
                            padding: '12px 24px',
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#FFFFFF',
                            background: '#FF7426',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            transition: 'all 0.3s ease',
                            fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                            marginTop: 4
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#E65A1F';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(255, 116, 38, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#FF7426';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {isArabic ? 'إرسال الرسالة' : 'Send Message'}
                          <FaPaperPlane size={12} />
                        </button>
                      </div>
                    </form>
                  </div>
                </FadeInSection>

                {/* Right: Contact Information */}
                <FadeInSection delay={0.4}>
                  <div>
                    <h3 style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#0F172A',
                      marginBottom: 24
                    }}>
                      {isArabic ? 'معلومات التواصل' : 'Get in Touch'}
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                      {/* Email */}
                      <div style={{
                        display: 'flex',
                        gap: 16,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FF7426',
                          flexShrink: 0
                        }}>
                          <FaEnvelope size={18} />
                        </div>
                        <div>
                          <h4 style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#0F172A',
                            marginBottom: 4,
                            marginTop: 0
                          }}>
                            {isArabic ? 'البريد الإلكتروني' : 'Email'}
                          </h4>
                          <a
                            href="mailto:support@taealam.bh"
                            style={{
                              fontSize: 14,
                              color: '#64748B',
                              textDecoration: 'none',
                              transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#FF7426'}
                            onMouseLeave={(e) => e.target.style.color = '#64748B'}
                          >
                            support@taealam.bh
                          </a>
                        </div>
                      </div>

                      {/* Phone */}
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FF7426',
                          flexShrink: 0
                        }}>
                          <FaPhone size={18} />
                        </div>
                        <div>
                          <h4 style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#0F172A',
                            marginBottom: 4,
                            marginTop: 0
                          }}>
                            {isArabic ? 'الهاتف' : 'Phone'}
                          </h4>
                          <a
                            href="tel:+97312345678"
                            style={{
                              fontSize: 14,
                              color: '#64748B',
                              textDecoration: 'none',
                              transition: 'color 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#FF7426'}
                            onMouseLeave={(e) => e.target.style.color = '#64748B'}
                          >
                            {isArabic ? toArabicNumerals('+973 3903 0980', isArabic) : '+973 3903 0980'}
                          </a>
                        </div>
                      </div>

                      {/* Address */}
                      <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start'
                      }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          background: 'rgba(255, 116, 38, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FF7426',
                          flexShrink: 0
                        }}>
                          <FaMapMarkerAlt size={18} />
                        </div>
                        <div>
                          <h4 style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#0F172A',
                            marginBottom: 4,
                            marginTop: 0
                          }}>
                            {isArabic ? 'العنوان' : 'Address'}
                          </h4>
                          <p style={{
                            fontSize: 14,
                            color: '#64748B',
                            margin: 0,
                            lineHeight: 1.5
                          }}>
                            {isArabic
                              ? 'المنامة، مملكة البحرين'
                              : 'Manama, Kingdom of Bahrain'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeInSection>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section id="contact-section" style={{
            background: '#F8FAFC',
            padding: isMobile ? '80px 20px' : '120px 40px'
          }}>
            <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
              <FadeInSection>
                <h2 style={{
                  fontSize: isMobile ? 28 : 42,
                  fontWeight: 800,
                  color: '#0F172A',
                  marginBottom: 16
                }}>
                  {t.ctaTitle}
                </h2>
                <p style={{
                  fontSize: 18,
                  color: '#64748B',
                  marginBottom: 40,
                  lineHeight: 1.7
                }}>
                  {t.ctaSubtitle}
                </p>
                <div style={{
                  display: 'flex',
                  gap: 16,
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    className="stripe-btn-primary"
                    onClick={() => navigate(isStudent ? '/signup' : '/pricing')}
                    style={{ padding: '16px 40px', fontSize: 16 }}
                  >
                    {t.ctaButton}
                    <FaArrowRight size={16} style={{ transform: isArabic ? 'rotate(180deg)' : 'none' }} />
                  </button>
                </div>
              </FadeInSection>
            </div>
          </section>

          {/* Footer */}
          <footer style={{
            background: '#0F172A',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            padding: isMobile ? '60px 20px 32px' : '80px 40px 40px'
          }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: isMobile ? 40 : 60,
                marginBottom: 60
              }}>
                {/* Logo & Description */}
                <div style={{ gridColumn: isMobile ? '1' : '1 / span 1' }}>
                  <img
                    src={logo}
                    alt="Taealam"
                    style={{
                      height: 40,
                      marginBottom: 20,
                      filter: 'brightness(0) invert(1)'
                    }}
                  />
                  <p style={{
                    fontSize: 14,
                    color: '#94A3B8',
                    lineHeight: 1.7,
                    marginBottom: 24
                  }}>
                    {t.footerDescription}
                  </p>
                </div>

                {/* Platform */}
                <div>
                  <h4 style={{
                    color: 'white',
                    fontWeight: 700,
                    marginBottom: 20,
                    fontSize: 14,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {t.footerPlatform}
                  </h4>
                  {t.footerLinks.platform.map((link, idx) => {
                    const handleClick = (e) => {
                      if (link.isAnchor) {
                        e.preventDefault();
                        const element = document.querySelector(link.path);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      } else if (link.isExternal) {
                        window.location.href = link.path;
                      } else {
                        navigate(link.path);
                      }
                    };

                    return (
                      <a
                        key={idx}
                        href={link.isAnchor ? link.path : (link.isExternal ? link.path : link.path)}
                        onClick={handleClick}
                        target={link.isExternal ? '_blank' : undefined}
                        rel={link.isExternal ? 'noopener noreferrer' : undefined}
                        style={{
                          display: 'block',
                          color: '#94A3B8',
                          fontSize: 14,
                          marginBottom: 12,
                          textDecoration: 'none',
                          transition: 'color 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#FF7426'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
                      >
                        {link.label}
                      </a>
                    );
                  })}
                </div>

                {/* Resources */}
                <div>
                  <h4 style={{
                    color: 'white',
                    fontWeight: 700,
                    marginBottom: 20,
                    fontSize: 14,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {t.footerResources}
                  </h4>
                  {t.footerLinks.resources.map((link, idx) => {
                    const handleClick = (e) => {
                      e.preventDefault();
                      navigate(link.path);
                    };

                    return (
                      <a
                        key={idx}
                        href={link.path}
                        onClick={handleClick}
                        style={{
                          display: 'block',
                          color: '#94A3B8',
                          fontSize: 14,
                          marginBottom: 12,
                          textDecoration: 'none',
                          transition: 'color 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#FF7426'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
                      >
                        {link.label}
                      </a>
                    );
                  })}
                </div>

                {/* Legal */}
                <div>
                  <h4 style={{
                    color: 'white',
                    fontWeight: 700,
                    marginBottom: 20,
                    fontSize: 14,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {t.footerLegal}
                  </h4>
                  {t.footerLinks.legal.map((link, idx) => {
                    const handleClick = (e) => {
                      e.preventDefault();
                      navigate(link.path);
                    };

                    return (
                      <a
                        key={idx}
                        href={link.path}
                        onClick={handleClick}
                        style={{
                          display: 'block',
                          color: '#94A3B8',
                          fontSize: 14,
                          marginBottom: 12,
                          textDecoration: 'none',
                          transition: 'color 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#FF7426'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
                      >
                        {link.label}
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Bottom bar */}
              <div style={{
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                paddingTop: 32,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16
              }}>
                <p style={{
                  color: '#64748B',
                  fontSize: 13,
                  margin: 0
                }}>
                  © {new Date().getFullYear()} Taealam. {t.footerCopyright}
                </p>
              </div>
            </div>
          </footer>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

